import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, doc, updateDoc, setDoc, deleteField } from "firebase/firestore";
import { db } from "@/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { queryBackend, adminResyncForm } from "@/services/backendSync";
import { flushPendingMeasurementsToGCP, syncMetricsToGCP } from "@/services/measurements";
import { buildF2Firestore, buildF3Firestore, buildF4Firestore, buildF5Firestore } from "@/data/loinc";
import {
  Users, ShieldCheck, ShieldOff, UserCheck, UserX, Search, RefreshCw,
  Loader2, ArrowLeftRight, X, CheckCircle2, AlertCircle, MinusCircle, Upload, Download, Trash2, Eye, Mail, ShieldAlert, Stethoscope,
} from "lucide-react";

export function isGcpF5Observation(o) {
  if (!o || !o.code) return false;
  const c = String(o.code);
  // Excluir explícitamente biomarcadores rPPG de FaceHeart (we360-stress-index, we360-wellness)
  if (c === "we360-stress-index" || c === "we360-wellness") return false;
  return (
    c.startsWith("we360:gad5") ||
    c.startsWith("we360:stress") ||
    c.startsWith("we360:wb") ||
    c.startsWith("we360:wellbeing") ||
    c.startsWith("we360:f5-")
  );
}

function isFormPayloadNonEmpty(questionnaireId, obj) {
  if (!obj || typeof obj !== "object") return false;
  if (questionnaireId === "F5-Mental-Health") {
    const ans = obj.ansiedad?.respuestas || obj.anxiety?.responses || [];
    const est = obj.estres?.respuestas || obj.stress?.responses || [];
    const bie = obj.bienestar?.respuestas || obj.wellbeing?.responses || [];
    return ans.length > 0 || est.length > 0 || bie.length > 0;
  }
  if (questionnaireId === "F4-Nutrition") {
    const cons = obj.consumo || obj.foodIntake || [];
    const cond = obj.conductas || obj.behaviors || [];
    return cons.length > 0 || cond.length > 0 || obj.scoreCalidadDieta != null || obj.dietQualityScore != null;
  }
  if (questionnaireId === "F2-PAR-Q-Physical") {
    const obs = obj.observaciones || obj.observations || [];
    const parq = obj.parqResponses || [];
    return obs.length > 0 || parq.length > 0 || obj.caminata6min != null;
  }
  if (questionnaireId === "F3-IPAQ") {
    const obs = obj.observaciones || obj.observations || [];
    return obs.length > 0 || obj.metTotal != null || Boolean(obj.vigorosa || obj.vigorous || obj.caminata || obj.walking);
  }
  return Object.keys(obj).length > 0;
}

function getFbFormData(fb, questionnaireId, legacyKey, builderFn) {
  if (!fb) return {};
  const otherEntry = fb.other?.find((o) => o && o.questionnaire === questionnaireId);
  const rawFromOther = otherEntry?.data;
  const fromOther =
    rawFromOther && typeof rawFromOther === "object"
      ? {
          ...rawFromOther,
          completedAt: rawFromOther.completedAt || rawFromOther.createdAt || rawFromOther.date || otherEntry?.completedAt || otherEntry?.date || null,
        }
      : null;
  const rawLegacy = fb[legacyKey];

  const otherValid = isFormPayloadNonEmpty(questionnaireId, fromOther);
  const legacyValid = isFormPayloadNonEmpty(questionnaireId, rawLegacy);

  let raw = null;
  if (otherValid && legacyValid) {
    const otherTs = fromOther.completedAt || fromOther.createdAt || fromOther.date || "";
    const legacyTs = rawLegacy.completedAt || rawLegacy.createdAt || rawLegacy.date || "";
    raw = legacyTs > otherTs ? rawLegacy : fromOther;
  } else if (otherValid) {
    raw = fromOther;
  } else if (legacyValid) {
    raw = rawLegacy;
  } else {
    raw = fromOther || rawLegacy || null;
  }

  if (!raw || typeof raw !== "object") return {};

  const isAlreadyBuilt =
    (questionnaireId === "F2-PAR-Q-Physical" && Boolean(raw.observaciones || raw.observations)) ||
    (questionnaireId === "F3-IPAQ" && Boolean(raw.observaciones || raw.observations)) ||
    (questionnaireId === "F4-Nutrition" && Boolean(raw.consumo?.[0]?.code || raw.foodIntake?.[0]?.code)) ||
    (questionnaireId === "F5-Mental-Health" && Boolean(typeof raw.ansiedad?.respuestas?.[0] === "object" || typeof raw.anxiety?.responses?.[0] === "object"));

  if (!isAlreadyBuilt && builderFn) {
    try { return builderFn(raw); } catch { return raw; }
  }
  return raw;
}

// ── Helper to evaluate local Outbox & FHIR sync state per user ──
export function getUserFhirSyncInfo(u) {
  if (!u) {
    return {
      status: "EMPTY",
      label: "Sin datos",
      color: "#64748B",
      bg: "#F1F5F9",
      border: "#E2E8F0",
      lastSyncAt: null,
      pendingF1: false,
      pendingMetrics: false,
    };
  }
  const p = u.profile || {};
  const pendingF1 = Boolean(
    u.pendingFhirF1 || p.pendingFhirF1 ||
    u.pendingFhirF2 || p.pendingFhirF2 ||
    u.pendingFhirF3 || p.pendingFhirF3 ||
    u.pendingFhirF4 || p.pendingFhirF4 ||
    u.pendingFhirF5 || p.pendingFhirF5
  );
  const pendingMetrics = Boolean(u.pendingFhirMetrics || p.pendingFhirMetrics);
  const rawStatus = u.fhirSyncStatus || p.fhirSyncStatus || null;
  const lastSyncAt = u.lastFhirSyncAt || p.lastFhirSyncAt || p.fhirSyncedAt || null;
  const isReconciled = Boolean(u.fhirReconciled || p.fhirReconciled || lastSyncAt);

  const hasLocalClinical = Boolean(
    p.completedAt ||
    (Array.isArray(p.observations) && p.observations.length > 0) ||
    (Array.isArray(p.vitalSigns) && p.vitalSigns.length > 0) ||
    (Array.isArray(p.measurementsHistory) && p.measurementsHistory.length > 0) ||
    (Array.isArray(p.other) && p.other.length > 0) ||
    p.heartRate != null ||
    p.peso != null ||
    p.paSistolica != null ||
    p.f2Results ||
    p.f3Results ||
    p.f4Results ||
    p.f5Results
  );

  if (rawStatus === "PENDING_FHIR" || pendingF1 || pendingMetrics) {
    return {
      status: "PENDING_FHIR",
      label: "Pendiente FHIR",
      color: "#B45309",
      bg: "#FEF3C7",
      border: "#FCD34D",
      lastSyncAt,
      pendingF1,
      pendingMetrics,
    };
  }

  if (rawStatus === "SYNCED" || isReconciled) {
    return {
      status: "SYNCED",
      label: "Sincronizado FHIR",
      color: "#166534",
      bg: "#DCFCE7",
      border: "#86EFAC",
      lastSyncAt,
      pendingF1: false,
      pendingMetrics: false,
    };
  }

  if (hasLocalClinical) {
    return {
      status: "UNVERIFIED",
      label: "Verificar FHIR",
      color: "#0369A1",
      bg: "#E0F2FE",
      border: "#BAE6FD",
      lastSyncAt: null,
      pendingF1: false,
      pendingMetrics: false,
    };
  }

  return {
    status: "EMPTY",
    label: "Sin datos",
    color: "#64748B",
    bg: "#F1F5F9",
    border: "#E2E8F0",
    lastSyncAt: null,
    pendingF1: false,
    pendingMetrics: false,
  };
}

// ── Sync comparison helpers ─────────────────────────────────
function extractFirebaseF1(profile) {
  if (!profile) return null;
  
  // Handle FHIR format (new)
  if (profile.resourceType === "Patient") {
    const observations = profile.observations || [];
    const conditions = profile.conditions || [];
    const medications = (profile.medications?.length > 0 ? profile.medications : profile.medicamentos) || [];
    
    const findObs = (code) => observations.find((o) => o.code === code);
    
    // Count vital signs
    const vitalSignCodes = ["29463-7", "8302-2", "8280-0", "8480-6", "8462-4", "1558-6", "2708-6", 
      "8867-4", "80404-7", "9279-1", "we360-stress-index", "we360-cv-age", "we360-cv-bmi", 
      "we360-cvd-risk", "we360-wellness", "we360-ans-pns", "we360-ans-sns",
      "4548-4", "718-7", "2093-3", "we360-shock-index", "we360-prq", "we360-hrv-rmssd", "we360-hrv-sdnn"];
    const vitalSignsCount = observations.filter((o) => vitalSignCodes.includes(o.code) && o.value != null).length;
    
    const fallbackName = `${profile.firstName || profile.nombres || ""} ${profile.lastName || profile.apellidos || ""}`.trim();
    const resolvedBirthDate = profile.birthDate || profile.fechaNacimiento || null;
    const resolvedSex =
      profile.gender === "male"
        ? "Masculino"
        : profile.gender === "female"
          ? "Femenino"
          : (profile.sexo || null);

    return {
      name: profile.name || fallbackName || null,
      birthDate: resolvedBirthDate,
      sex: resolvedSex,
      weight: findObs("29463-7")?.value || null,
      height: findObs("8302-2")?.value || null,
      bpSys: findObs("8480-6")?.value || null,
      bpDia: findObs("8462-4")?.value || null,
      glucose: findObs("1558-6")?.value || null,
      spo2: findObs("2708-6")?.value || null,
      vitalSignsCount,
      conditions: conditions.length,
      medications: medications.length,
      hasF1: !!(profile.completedAt || resolvedBirthDate || resolvedSex || observations.length > 0),
      hasF2: !!(profile.other?.find((o) => o.questionnaire === "F2-PAR-Q-Physical") || profile.f2Results || profile.parq),
      hasF3: !!(profile.other?.find((o) => o.questionnaire === "F3-IPAQ") || profile.f3Results || profile.vigorous),
      hasF4: !!(profile.other?.find((o) => o.questionnaire === "F4-Nutrition") || profile.f4Results || profile.foodIntake),
      hasF5: !!(profile.other?.find((o) => o.questionnaire === "F5-Mental-Health") || profile.f5Results || profile.anxiety),
    };
  }
  
  // Handle legacy format (old)
  const vs = profile.vitalSigns || profile.signos || [];
  const findVS = (code) => { const o = vs.find((s) => s.code === code); return o?.value ?? null; };
  
  const vitalSignCodes = ["29463-7", "8302-2", "8280-0", "8480-6", "8462-4", "1558-6", "2708-6", 
    "8867-4", "80404-7", "9279-1", "we360-stress-index", "we360-cv-age", "we360-cv-bmi", 
    "we360-cvd-risk", "we360-wellness", "we360-ans-pns", "we360-ans-sns",
    "4548-4", "718-7", "2093-3", "we360-shock-index", "we360-prq", "we360-hrv-rmssd", "we360-hrv-sdnn"];

  // Count from vitalSigns array
  let vitalSignsCount = vs.filter((s) => vitalSignCodes.includes(s.code) && s.value != null).length;
  
  // Also check flat fields for FaceHeart metrics (fallback if not in vitalSigns array)
  const faceHeartFields = {
    "heartRate": "8867-4",
    "heartRateVariability": "80404-7",
    "respirationRate": "9279-1",
    "stressIndex": "we360-stress-index",
    "cardiovascularAge": "we360-cv-age",
    "cardiovascularBMI": "we360-cv-bmi",
    "cvdRisk": "we360-cvd-risk",
    "wellnessScore": "we360-wellness",
    "ansIndexPNS": "we360-ans-pns",
    "ansIndexSNS": "we360-ans-sns",
    "hba1c": "4548-4",
    "hemoglobin": "718-7",
    "cholesterol": "2093-3",
    "shockIndex": "we360-shock-index",
    "prq": "we360-prq",
    "hrvRMSSD": "we360-hrv-rmssd",
    "hrvSDNN": "we360-hrv-sdnn",
  };
  
  // Count FaceHeart metrics from flat fields if not already in vitalSigns array
  Object.entries(faceHeartFields).forEach(([field, code]) => {
    const inArray = vs.some((s) => s.code === code);
    if (!inArray && profile[field] != null) {
      vitalSignsCount++;
    }
  });
  
  return {
    name: `${profile.firstName || profile.nombres || ""} ${profile.lastName || profile.apellidos || ""}`.trim() || null,
    birthDate: profile.birthDate || profile.fechaNacimiento || null,
    sex: typeof profile.sex === "string" ? profile.sex : (profile.sex?.displayES || profile.sex?.display || profile.sexo || null),
    weight: findVS("29463-7") ?? profile.peso ?? null,
    height: findVS("8302-2") ?? profile.talla ?? null,
    bpSys: findVS("8480-6") ?? profile.paSistolica ?? null,
    bpDia: findVS("8462-4") ?? profile.paDiastolica ?? null,
    glucose: findVS("1558-6") ?? profile.glucemia ?? null,
    spo2: findVS("2708-6") ?? profile.oximetria ?? null,
    vitalSignsCount,
    conditions: dedupAndFilterGcpConditions(profile.conditions || profile.condiciones || [], vs).length,
    medications: (profile.medications || profile.medicamentos || []).length,
    hasF1: !!(profile.completedAt || profile.birthDate || profile.fechaNacimiento || profile.sex || profile.sexo || vitalSignsCount > 0),
    hasF2: !!(profile.other?.find((o) => o.questionnaire === "F2-PAR-Q-Physical") || profile.f2Results || profile.parq),
    hasF3: !!(profile.other?.find((o) => o.questionnaire === "F3-IPAQ") || profile.f3Results || profile.vigorous),
    hasF4: !!(profile.other?.find((o) => o.questionnaire === "F4-Nutrition") || profile.f4Results || profile.foodIntake),
    hasF5: !!(profile.other?.find((o) => o.questionnaire === "F5-Mental-Health") || profile.f5Results || profile.anxiety),
  };
}

const BMI_EXCLUSIVE_CODES = ["248342006", "238131007", "414916001"];

function dedupAndFilterGcpConditions(rawConds = [], latestObs = [], fbConds = []) {
  const byCode = new Map();
  rawConds.forEach((c) => {
    const code = c?.code || c?.snomedCode || c?.codigoSNOMED;
    if (code) byCode.set(code, { ...c, code });
  });

  // Resolver condiciones de IMC según Peso y Talla actuales
  const w = parseFloat(latestObs.find((o) => o?.code === "29463-7")?.value);
  const h = parseFloat(latestObs.find((o) => o?.code === "8302-2")?.value);
  if (w > 0 && h > 0) {
    const bmi = w / Math.pow(h / 100, 2);
    let activeBmiCode = null;
    if (bmi >= 30) activeBmiCode = "414916001";
    else if (bmi >= 25) activeBmiCode = "238131007";
    else if (bmi < 18.5) activeBmiCode = "248342006";
    BMI_EXCLUSIVE_CODES.forEach((c) => {
      if (c !== activeBmiCode) byCode.delete(c);
    });
  } else {
    const presentBmiCodes = BMI_EXCLUSIVE_CODES.filter((c) => byCode.has(c));
    if (presentBmiCodes.length > 1) {
      const fbBmi = (fbConds || [])
        .map((fc) => fc?.code || fc?.snomedCode || fc?.codigoSNOMED)
        .find((c) => BMI_EXCLUSIVE_CODES.includes(c));
      if (fbBmi && byCode.has(fbBmi)) {
        presentBmiCodes.forEach((c) => {
          if (c !== fbBmi) byCode.delete(c);
        });
      }
    }
  }

  // Resolver PA elevada (24184005: 120-129 / <80): si la PA actual es normal (<120 y <80), no aplica PA elevada
  const sys = parseFloat(latestObs.find((o) => o?.code === "8480-6")?.value);
  const dia = parseFloat(latestObs.find((o) => o?.code === "8462-4")?.value);
  if (sys > 0 && dia > 0) {
    const isElevatedBp = sys >= 120 && sys < 130 && dia < 80;
    if (!isElevatedBp) {
      byCode.delete("24184005");
    }
  }

  return Array.from(byCode.values());
}

function cleanGcpMedLabel(med) {
  if (!med) return null;
  const rawName = med.name || med.nombre;
  const rawDosage = med.dosage || med.dosificacion || med.dosis;
  if (rawName) {
    return `${rawName} ${rawDosage || ""}`.trim();
  }
  const rawDisplay = String(med.display || "").trim();
  if (!rawDisplay) return "?";
  // Formato de GCP Core Services: "Nombre (Marca) - Dosis" o "Nombre () - null"
  const match = rawDisplay.match(/^(.+?)\s*\(([^)]*)\)\s*-\s*(.*)$/);
  if (match) {
    const baseName = match[1].trim();
    const dosagePart = match[3].trim();
    const cleanDosage = dosagePart && dosagePart !== "null" && dosagePart !== "undefined" ? dosagePart : "";
    return `${baseName}${cleanDosage ? ` ${cleanDosage}` : ""}`.trim();
  }
  return rawDisplay
    .replace(/\s*\(\s*\)/g, "")
    .replace(/\s*-\s*(null|undefined)\s*$/i, "")
    .trim();
}

function dedupGcpMedications(rawMeds = []) {
  const byAtc = new Map();
  const withoutAtc = [];
  rawMeds.forEach((m) => {
    const atc = m?.code || m?.atcCode || m?.codigoATC;
    if (atc) {
      byAtc.set(atc, { ...m, atcCode: atc });
    } else if (m) {
      withoutAtc.push(m);
    }
  });
  return [...Array.from(byAtc.values()), ...withoutAtc];
}

function parseGcpMedForFirestore(med) {
  if (!med) return null;
  const atcCode = med.code || med.atcCode || med.codigoATC || null;
  let name = med.name || med.nombre || "";
  let brandName = med.brandName || med.nombreComercial || null;
  let dosage = med.dosage || med.dosificacion || med.dosis || "";
  if (!name && med.display) {
    const rawDisplay = String(med.display || "").trim();
    const match = rawDisplay.match(/^(.+?)\s*\(([^)]*)\)\s*-\s*(.*)$/);
    if (match) {
      name = match[1].trim();
      brandName = match[2].trim() || null;
      const dPart = match[3].trim();
      dosage = dPart && dPart !== "null" && dPart !== "undefined" ? dPart : "";
    } else {
      name = rawDisplay
        .replace(/\s*\(\s*\)/g, "")
        .replace(/\s*-\s*(null|undefined)\s*$/i, "")
        .trim();
    }
  }
  if (!name) return null;
  return {
    resourceType: "MedicationStatement",
    status: "active",
    name,
    atcCode,
    code: atcCode,
    dosage,
    brandName,
    source: atcCode ? "curated" : "free_text",
  };
}

function extractGcpData(gcpData) {
  if (!gcpData?.profile) return null;
  const p = gcpData.profile;
  const patient = p.patient || {};
  const allObs = Array.isArray(p.observations) ? p.observations : [];
  
  // Group observations by code and keep only the most recent one per code
  const obsMap = new Map();
  allObs.forEach((o) => {
    if (!o || typeof o !== "object" || !o.code) return;
    const existing = obsMap.get(o.code);
    const currentDate = o.effectiveDateTime || o.issued || o.date || "";
    const existingDate = existing?.effectiveDateTime || existing?.issued || existing?.date || "";
    // Keep the observation with the most recent date (or later entry on tie)
    if (!existing || currentDate >= existingDate) {
      obsMap.set(o.code, o);
    }
  });
  const obs = Array.from(obsMap.values());
  const normalizedConds = dedupAndFilterGcpConditions(p.conditions || [], obs);
  const normalizedMeds = dedupGcpMedications(p.medications || []);
  
  const findObs = (code) => { const o = obs.find((x) => x.code === code); return o?.value ?? null; };
  const formCodes = obs.map((o) => o.code || "").filter(Boolean);
  
  // Count vital signs with actual values (excluding lifestyle codes)
  const vitalSignCodes = ["29463-7", "8302-2", "8280-0", "8480-6", "8462-4", "1558-6", "2708-6", 
    "8867-4", "80404-7", "9279-1", "we360-stress-index", "we360-cv-age", "we360-cv-bmi", 
    "we360-cvd-risk", "we360-wellness", "we360-ans-pns", "we360-ans-sns",
    "4548-4", "718-7", "2093-3", "we360-shock-index", "we360-prq", "we360-hrv-rmssd", "we360-hrv-sdnn"];
  const vitalSignsCount = obs.filter((o) => vitalSignCodes.includes(o.code) && o.value != null).length;
  
  return {
    name: patient.name || null,
    birthDate: patient.birthDate || null,
    sex: patient.gender === "male" ? "Masculino" : patient.gender === "female" ? "Femenino" : (patient.gender || null),
    weight: findObs("29463-7"),
    height: findObs("8302-2"),
    bpSys: findObs("8480-6"),
    bpDia: findObs("8462-4"),
    glucose: findObs("1558-6"),
    spo2: findObs("2708-6"),
    vitalSignsCount, // Count of most recent vital signs only
    conditions: normalizedConds.length,
    medications: normalizedMeds.length,
    hasF1: !!(patient.name || obs.length > 0),
    hasF2: formCodes.some((c) => ["64098-7", "we360-sentadillas-30s", "we360-flexiones-max", "we360-flexibilidad"].includes(c) || c.startsWith("we360:parq-")),
    hasF3: formCodes.some((c) => ["we360-met-total", "we360-ipaq-nivel", "we360-sedentarismo"].includes(c) || c.startsWith("we360:ipaq-")),
    hasF4: formCodes.some((c) => c.startsWith("we360:food-") || c.startsWith("we360:behav-") || c === "we360:hydration" || ["we360:fruit", "we360:vegetables", "we360:processed"].includes(c)),
    hasF5: obs.some((o) => isGcpF5Observation(o)),
  };
}

function compareFields(fb, gcp) {
  const fields = [
    { key: "name", label: "Nombre" },
    { key: "birthDate", label: "Fecha nacimiento" },
    { key: "sex", label: "Sexo" },
    { key: "weight", label: "Peso (kg)" },
    { key: "height", label: "Talla (cm)" },
    { key: "bpSys", label: "PA Sist." },
    { key: "bpDia", label: "PA Diast." },
    { key: "glucose", label: "Glucemia" },
    { key: "spo2", label: "SpO2" },
    { key: "vitalSignsCount", label: "Signos Vitales (total)" }, // NEW: Compare vital signs count
    { key: "conditions", label: "Condiciones" },
    { key: "medications", label: "Medicamentos" },
    { key: "hasF1", label: "Form F1" },
    { key: "hasF2", label: "Form F2" },
    { key: "hasF3", label: "Form F3" },
    { key: "hasF4", label: "Form F4" },
    { key: "hasF5", label: "Form F5" },
  ];
  return fields.map((f) => {
    const fbVal = fb?.[f.key];
    const gcpVal = gcp?.[f.key];
    const fbStr = fbVal == null ? "-" : String(fbVal);
    const gcpStr = gcpVal == null ? "-" : String(gcpVal);
    let status; // match | mismatch | missing_gcp | missing_fb | both_empty
    if (fbStr === "-" && gcpStr === "-") status = "both_empty";
    else if (fbStr === gcpStr) status = "match";
    else if (gcpStr === "-" || gcpStr === "null") status = "missing_gcp";
    else if (fbStr === "-" || fbStr === "null") status = "missing_fb";
    else status = "mismatch";
    return { ...f, fbVal: fbStr, gcpVal: gcpStr, status };
  });
}

// ── Semantic detail compare ─────────────────────────────────
// Extracts values from both Firebase & GCP by code/meaning and groups them
function extractDetailedComparison(fbProfile, gcpData, fallbackEmail = null) {
  const groups = [];
  const fb = fbProfile || {};
  const gcp = gcpData?.profile || {};
  const gcpPatient = gcp.patient || {};
  const allGcpObs = Array.isArray(gcp.observations) ? gcp.observations : [];

  // Filter GCP observations: keep only the most recent one per code
  const obsMap = new Map();
  const displayObsMap = new Map();
  allGcpObs.forEach((o) => {
    if (!o || typeof o !== "object") return;
    const currentDate = o.effectiveDateTime || o.issued || o.date || "";
    if (o.code) {
      const existing = obsMap.get(o.code);
      const existingDate = existing?.effectiveDateTime || existing?.issued || existing?.date || "";
      if (!existing || currentDate >= existingDate) {
        obsMap.set(o.code, o);
      }
    }
    const disp = (typeof o.display === "string" ? o.display : (o.display ? String(o.display) : "")).trim();
    if (disp) {
      const existingDisp = displayObsMap.get(disp);
      const existingDispDate = existingDisp?.effectiveDateTime || existingDisp?.issued || existingDisp?.date || "";
      if (!existingDisp || currentDate >= existingDispDate) {
        displayObsMap.set(disp, o);
      }
    }
  });
  const gcpObs = Array.from(obsMap.values());
  const findGcpByDisplay = (display) => {
    const o = displayObsMap.get(display);
    return o ? (o.value ?? o.valueCode?.display ?? null) : null;
  };

  // Helper: find vital sign by code in Firebase (supports both FHIR and legacy)
  const fbVS = fb.observations || fb.vitalSigns || fb.signos || [];
  const findFbVS = (code) => {
    const o = fbVS.find((s) => s.code === code);
    if (!o) return null;
    // Return the Spanish value, not the English display from valueCode
    return o.value ?? null;
  };

  const fbConds = dedupAndFilterGcpConditions(fb.conditions || fb.condiciones || [], fbVS);
  const gcpConds = dedupAndFilterGcpConditions(gcp.conditions || [], gcpObs, fbConds);
  const gcpMeds = dedupGcpMedications(gcp.medications || []);

  // Helper: find observation by code in GCP (most recent only)
  const findGcpObs = (code) => {
    const o = gcpObs.find((x) => x.code === code);
    return o ? (o.value ?? o.valueCode?.display ?? null) : null;
  };

  // ── Demographics ──
  const fbName = fb.name || `${fb.firstName || fb.nombres || ""} ${fb.lastName || fb.apellidos || ""}`.trim();
  const fbSex = fb.gender === "male" ? "Masculino" : fb.gender === "female" ? "Femenino" : 
                (typeof fb.sex === "string" ? fb.sex : (fb.sex?.displayES || fb.sex?.display || fb.sexo || null));
  const gcpSex = gcpPatient.gender === "male" ? "Masculino" : gcpPatient.gender === "female" ? "Femenino" : gcpPatient.gender;
  
  groups.push({
    title: "Datos demográficos",
    rows: [
      { label: "Nombre", fb: fbName || null, gcp: gcpPatient.name || null },
      { label: "Fecha de nacimiento", fb: fb.birthDate || fb.fechaNacimiento || null, gcp: gcpPatient.birthDate || null },
      { label: "Sexo", fb: fbSex, gcp: gcpSex },
      { label: "Email", fb: fb.email || fallbackEmail || null, gcp: gcpPatient.email || null },
    ],
  });

  // ── Vital Signs (LOINC codes) ──
  const vitalDefs = [
    { code: "29463-7", label: "Peso", unit: "kg", fbKey: "peso" },
    { code: "8302-2", label: "Talla", unit: "cm", fbKey: "talla" },
    { code: "8280-0", label: "Cintura", unit: "cm", fbKey: "cintura" },
    { code: "8480-6", label: "PA Sistólica", unit: "mmHg", fbKey: "paSistolica" },
    { code: "8462-4", label: "PA Diastólica", unit: "mmHg", fbKey: "paDiastolica" },
    { code: "1558-6", label: "Glucemia", unit: "mg/dL", fbKey: "glucemia" },
    { code: "2708-6", label: "SpO2", unit: "%", fbKey: "oximetria" },
  ];
  groups.push({
    title: "Signos vitales (LOINC)",
    rows: vitalDefs.map((d) => ({
      label: `${d.label} [${d.code}]`,
      fb: findFbVS(d.code) ?? fb[d.fbKey] ?? null,
      gcp: findGcpObs(d.code),
      unit: d.unit,
    })),
  });

  // ── Lifestyle (LOINC codes) ──
  const lifeDefs = [
    { code: "72166-2", label: "Tabaco", fbKey: "tabaco" },
    { code: "74013-4", label: "Alcohol", fbKey: "alcohol" },
    { code: "93832-4", label: "Sueño", fbKey: "sueno", unit: "h" },
  ];
  groups.push({
    title: "Estilo de vida (LOINC)",
    rows: lifeDefs.map((d) => ({
      label: `${d.label} [${d.code}]`,
      fb: findFbVS(d.code) ?? fb[d.fbKey] ?? null,
      gcp: findGcpObs(d.code),
      unit: d.unit,
    })),
  });

  // ── Cardiovascular Metrics (FaceHeart SDK) ──
  const cardiovascularDefs = [
    { code: "8867-4", label: "Frecuencia cardíaca", unit: "lpm", fbKey: "heartRate" },
    { code: "80404-7", label: "Variabilidad (HRV)", unit: "ms", fbKey: "heartRateVariability" },
    { code: "9279-1", label: "Frecuencia respiratoria", unit: "rpm", fbKey: "respirationRate" },
    { code: "we360-stress-index", label: "Índice de estrés", unit: null, fbKey: "stressIndex" },
    { code: "we360-cv-age", label: "Edad cardiovascular", unit: "años", fbKey: "cardiovascularAge" },
    { code: "we360-cv-bmi", label: "BMI cardiovascular", unit: null, fbKey: "cardiovascularBMI" },
    { code: "we360-cvd-risk", label: "Riesgo cardiovascular", unit: "%", fbKey: "cvdRisk" },
    { code: "we360-wellness", label: "Puntuación bienestar", unit: null, fbKey: "wellnessScore" },
    { code: "we360-ans-pns", label: "Índice ANS Parasimpático", unit: null, fbKey: "ansIndexPNS" },
    { code: "we360-ans-sns", label: "Índice ANS Simpático", unit: null, fbKey: "ansIndexSNS" },
    { code: "4548-4", label: "HbA1c estimada", unit: "%", fbKey: "hba1c" },
    { code: "718-7", label: "Hemoglobina estimada", unit: "g/dL", fbKey: "hemoglobin" },
    { code: "2093-3", label: "Colesterol estimado", unit: "mg/dL", fbKey: "cholesterol" },
    { code: "we360-shock-index", label: "Índice de shock", unit: null, fbKey: "shockIndex" },
    { code: "we360-prq", label: "Cociente PRQ", unit: null, fbKey: "prq" },
    { code: "we360-hrv-rmssd", label: "HRV RMSSD", unit: "ms", fbKey: "hrvRMSSD" },
    { code: "we360-hrv-sdnn", label: "HRV SDNN", unit: "ms", fbKey: "hrvSDNN" },
  ];
  const cardiovascularRows = cardiovascularDefs.map((d) => ({
    label: `${d.label} [${d.code}]`,
    fb: findFbVS(d.code) ?? fb[d.fbKey] ?? null,
    gcp: findGcpObs(d.code),
    unit: d.unit,
  })).filter((r) => r.fb !== null || r.gcp !== null); // Solo mostrar si hay datos
  if (cardiovascularRows.length > 0) {
    groups.push({
      title: "Métricas Cardiovasculares (We360 FaceScan)",
      rows: cardiovascularRows,
    });
  }

  // ── Conditions ──
  const maxConds = Math.max(fbConds.length, gcpConds.length);
  if (maxConds > 0) {
    const condRows = [];
    // Match by SNOMED code when possible
    const gcpCondMap = {};
    gcpConds.forEach((c) => { if (c.code) gcpCondMap[c.code] = c; });
    const matchedGcpCodes = new Set();
    fbConds.forEach((fc, i) => {
      const code = fc.code || fc.snomedCode || fc.codigoSNOMED;
      const gcpMatch = code ? gcpCondMap[code] : gcpConds[i];
      if (gcpMatch?.code) matchedGcpCodes.add(gcpMatch.code);
      const fbLabel = fc.display || fc.name || fc.nombre || "?";
      const gcpLabel = gcpMatch ? (gcpMatch.display || gcpMatch.name || "?") : null;
      condRows.push({
        label: code ? `[${code}]` : `#${i + 1}`,
        fb: fbLabel,
        gcp: gcpLabel,
      });
    });
    // Add unmatched GCP conditions
    gcpConds.forEach((gc) => {
      if (gc.code && !matchedGcpCodes.has(gc.code)) {
        condRows.push({ label: `[${gc.code}]`, fb: null, gcp: gc.display || gc.name || "?" });
      }
    });
    groups.push({ title: `Condiciones (${fbConds.length} FB / ${gcpConds.length} GCP)`, rows: condRows });
  }

  // ── Medications ──
  const fbMeds = (fb.medications?.length > 0 ? fb.medications : fb.medicamentos) || [];
  const maxMeds = Math.max(fbMeds.length, gcpMeds.length);
  if (maxMeds > 0) {
    const medRows = [];
    const gcpMedMap = {};
    gcpMeds.forEach((m) => { 
      const atc = m.code || m.atcCode || m.codigoATC;
      if (atc) gcpMedMap[atc] = m;
    });
    const matchedGcpAtc = new Set();
    fbMeds.forEach((fm, i) => {
      const atc = fm.code || fm.atcCode || fm.codigoATC;
      const gcpMatch = atc ? gcpMedMap[atc] : gcpMeds[i];
      if (gcpMatch) {
        const matchAtc = gcpMatch.code || gcpMatch.atcCode || gcpMatch.codigoATC;
        if (matchAtc) matchedGcpAtc.add(matchAtc);
      }
      const fbLabel = `${fm.name || fm.nombre || "?"} ${fm.dosage || fm.dosificacion || fm.dosis || ""}`.trim();
      const gcpLabel = gcpMatch ? cleanGcpMedLabel(gcpMatch) : null;
      medRows.push({ label: atc ? `[${atc}]` : `#${i + 1}`, fb: fbLabel, gcp: gcpLabel });
    });
    gcpMeds.forEach((gm) => {
      const atc = gm.code || gm.atcCode || gm.codigoATC;
      if (atc && !matchedGcpAtc.has(atc)) {
        medRows.push({ label: `[${atc}]`, fb: null, gcp: cleanGcpMedLabel(gm) });
      }
    });
    groups.push({ title: `Medicamentos (${fbMeds.length} FB / ${gcpMeds.length} GCP)`, rows: medRows });
  }

  const maxObsDate = (items = []) => {
    let max = null;
    items.forEach((o) => {
      const d = o?.effectiveDateTime || o?.issued || o?.date || null;
      if (d && (!max || d > max)) max = d;
    });
    return max;
  };

  // ── F2 observations ──
  const fbF2 = getFbFormData(fb, "F2-PAR-Q-Physical", "f2Results", buildF2Firestore);
  const fbF2Obs = [
    ...(fbF2.observations || fbF2.observaciones || []),
    ...(fbF2.parqResponses || []),
  ];
  const f2GcpCodes = ["64098-7", "we360-sentadillas-30s", "we360-flexiones-max", "we360-flexibilidad", "we360:6mwt", "we360:squats-1min", "we360:pushups-1min"];
  const gcpF2Obs = gcpObs.filter((o) => f2GcpCodes.includes(o.code) || (o.code && o.code.startsWith("we360:parq")));
  const fbF2Date = fbF2.completedAt || fbF2.createdAt || fbF2.date || null;
  const gcpF2Date = maxObsDate([
    ...gcpF2Obs,
    displayObsMap.get("6-Minute Walk Classification"),
    displayObsMap.get("Squats Classification"),
    displayObsMap.get("Push-ups Classification"),
    displayObsMap.get("Flexibility Classification"),
  ]);
  if (fbF2Obs.length > 0 || gcpF2Obs.length > 0) {
    const f2Rows = [];
    // Match by code
    const allCodes = new Set([...fbF2Obs.map((o) => o.code), ...gcpF2Obs.map((o) => o.code)].filter(Boolean));
    allCodes.forEach((code) => {
      const fbO = fbF2Obs.find((o) => o.code === code);
      const gcpO = gcpF2Obs.find((o) => o.code === code);
      let fbVal = null;
      if (fbO) {
        if (fbO.value !== undefined && fbO.value !== null) {
          fbVal = fbO.value;
        } else if (fbO.valueCode) {
          // If GCP stores code (e.g. "normal" for flexibility), match code; otherwise display
          const gcpStr = gcpO?.value != null ? String(gcpO.value) : null;
          if (gcpStr && fbO.valueCode.code && gcpStr === String(fbO.valueCode.code)) {
            fbVal = fbO.valueCode.code;
          } else {
            fbVal = fbO.valueCode.display ?? fbO.valueCode.code ?? fbO.valueCode.displayES ?? null;
          }
        }
      }
      f2Rows.push({
        label: `[${code}]`,
        fb: fbVal,
        gcp: gcpO ? (gcpO.value ?? null) : null,
      });
    });
    // Add F2 classifications matched with uncoded GCP observations
    const f2ClassKeys = [
      { fb: "classification6mwt", fbEs: "clasificacion6mwt", gcpDisplay: "6-Minute Walk Classification", label: "Clasif. 6MWT" },
      { fb: "classificationSquats", fbEs: "clasificacionSentadillas", gcpDisplay: "Squats Classification", label: "Clasif. Sentadillas" },
      { fb: "classificationPushups", fbEs: "clasificacionFlexiones", gcpDisplay: "Push-ups Classification", label: "Clasif. Flexiones" },
      { fb: "classificationFlexibility", fbEs: "clasificacionFlexibilidad", gcpDisplay: "Flexibility Classification", label: "Clasif. Flexibilidad" },
    ];
    f2ClassKeys.forEach((k) => {
      const fbVal = fbF2[k.fb] ?? fbF2[k.fbEs] ?? null;
      const gcpVal = findGcpByDisplay(k.gcpDisplay);
      if (fbVal !== null || gcpVal !== null) {
        f2Rows.push({ label: k.label, fb: fbVal, gcp: gcpVal });
      }
    });
    const fbParqAlert = fbF2.parqAlert ?? fbF2.parqAlerta;
    const gcpParqItems = gcpF2Obs.filter((o) => o.code && o.code.startsWith("we360:parq"));
    const gcpParqAlert = gcpParqItems.length > 0
      ? gcpParqItems.some((o) => o.value === true || o.value === "true")
      : null;
    if (fbParqAlert !== undefined || gcpParqAlert !== null) {
      f2Rows.push({
        label: "PAR-Q Alerta",
        fb: fbParqAlert !== undefined ? String(fbParqAlert) : null,
        gcp: gcpParqAlert !== null ? String(gcpParqAlert) : null,
      });
    }
    groups.push({ title: "F2: Estado Físico", rows: f2Rows, fbDate: fbF2Date, gcpDate: gcpF2Date });
  }

  // ── F3 observations ──
  const fbF3 = getFbFormData(fb, "F3-IPAQ", "f3Results", buildF3Firestore);
  const f3Vig = fbF3.vigorosa || fbF3.vigorous;
  const f3Mod = fbF3.moderada || fbF3.moderate;
  const f3Walk = fbF3.caminata || fbF3.walking;
  const fbF3Obs = [
    ...(fbF3.observations || fbF3.observaciones || []),
    ...(f3Vig ? [{ code: "we360:ipaq-vigorous", value: f3Vig.metMin ?? f3Vig.metMinutes ?? 0 }] : []),
    ...(f3Mod ? [{ code: "we360:ipaq-moderate", value: f3Mod.metMin ?? f3Mod.metMinutes ?? 0 }] : []),
    ...(f3Walk ? [{ code: "we360:ipaq-walking", value: f3Walk.metMin ?? f3Walk.metMinutes ?? 0 }] : []),
  ];
  const f3GcpCodes = ["we360-met-total", "we360-ipaq-nivel", "we360-sedentarismo", "we360:ipaq-vigorous", "we360:ipaq-moderate", "we360:ipaq-walking"];
  const gcpF3Obs = gcpObs.filter((o) => f3GcpCodes.includes(o.code));
  const fbSitting = fbF3.tiempoSentado?.display || fbF3.sittingTime?.display || (typeof fbF3.tiempoSentado === "string" ? fbF3.tiempoSentado : null);
  const gcpSitting = findGcpByDisplay("Time Spent Sitting (IPAQ)");
  const fbF3Date = fbF3.completedAt || fbF3.createdAt || fbF3.date || null;
  const gcpF3Date = maxObsDate([
    ...gcpF3Obs,
    displayObsMap.get("Time Spent Sitting (IPAQ)"),
  ]);
  if (fbF3Obs.length > 0 || gcpF3Obs.length > 0 || fbSitting || gcpSitting) {
    const f3Rows = [];
    const allF3Codes = new Set([...fbF3Obs.map((o) => o.code), ...gcpF3Obs.map((o) => o.code)].filter(Boolean));
    allF3Codes.forEach((code) => {
      const fbO = fbF3Obs.find((o) => o.code === code);
      const gcpO = gcpF3Obs.find((o) => o.code === code);
      f3Rows.push({
        label: `[${code}]`,
        fb: fbO ? (fbO.value ?? fbO.valueCode?.display ?? fbO.valueCode?.code ?? null) : null,
        gcp: gcpO ? (gcpO.value ?? null) : null,
      });
    });
    if (fbSitting !== null || gcpSitting !== null) {
      f3Rows.push({ label: "Tiempo Sentado", fb: fbSitting, gcp: gcpSitting });
    }
    groups.push({ title: "F3: Actividad Física", rows: f3Rows, fbDate: fbF3Date, gcpDate: gcpF3Date });
  }

  // ── F4 observations ──
  const fbF4 = getFbFormData(fb, "F4-Nutrition", "f4Results", buildF4Firestore);
  const fbF4Items = [
    ...(fbF4.consumo || fbF4.foodIntake || []),
    ...(fbF4.conductas || fbF4.behaviors || []),
  ];
  const gcpF4Obs = gcpObs.filter((o) => o.code && (o.code.startsWith("we360:food-") || o.code.startsWith("we360:behav-") || o.code.startsWith("we360:fruit") || o.code.startsWith("we360:vegetable") || o.code.startsWith("we360:processed") || o.code === "we360:hydration"));
  const fbDietScore = fbF4.dietQualityScore ?? fbF4.scoreCalidadDieta ?? null;
  const gcpDietScore = findGcpByDisplay("Diet Quality Score");
  const fbBehavScore = fbF4.behaviorScore ?? fbF4.scoreConductas ?? null;
  const gcpBehavScore = findGcpByDisplay("Behavior Score");
  const rawFbHydration = fbF4.hydration || fbF4.hidratacion;
  const fbHydration = rawFbHydration?.display ?? (typeof rawFbHydration === "string" ? rawFbHydration : null);
  const gcpHydration = findGcpByDisplay("Hydration Status");
  const fbF4Date = fbF4.completedAt || fbF4.createdAt || fbF4.date || null;
  const gcpF4Date = maxObsDate([
    ...gcpF4Obs,
    displayObsMap.get("Diet Quality Score"),
    displayObsMap.get("Behavior Score"),
    displayObsMap.get("Hydration Status"),
  ]);
  if (fbF4Items.length > 0 || gcpF4Obs.length > 0 || fbDietScore !== null || gcpDietScore !== null) {
    const f4Rows = [];
    if (fbDietScore !== null || gcpDietScore !== null) {
      f4Rows.push({ label: "Score Dieta", fb: fbDietScore, gcp: gcpDietScore });
    }
    if (fbBehavScore !== null || gcpBehavScore !== null) {
      f4Rows.push({ label: "Score Conductas", fb: fbBehavScore, gcp: gcpBehavScore });
    }
    if (fbHydration !== null || gcpHydration !== null) {
      f4Rows.push({ label: "Hidratación", fb: fbHydration, gcp: gcpHydration });
    }
    const allF4Codes = new Set([...fbF4Items.map((i) => i.code), ...gcpF4Obs.map((o) => o.code)].filter(Boolean));
    allF4Codes.forEach((code) => {
      const fbItem = fbF4Items.find((i) => i.code === code);
      const gcpO = gcpF4Obs.find((o) => o.code === code);
      const fbVal = fbItem ? ((fbItem.frecuencia || fbItem.frequency)?.display ?? fbItem.value ?? null) : null;
      f4Rows.push({
        label: `[${code}]`,
        fb: fbVal,
        gcp: gcpO ? (gcpO.value ?? null) : null,
      });
    });
    groups.push({ title: "F4: Nutrición", rows: f4Rows, fbDate: fbF4Date, gcpDate: gcpF4Date });
  }

  // ── F5 observations ──
  const fbF5 = getFbFormData(fb, "F5-Mental-Health", "f5Results", buildF5Firestore);
  const f5Ans = fbF5.ansiedad || fbF5.anxiety || {};
  const f5Est = fbF5.estres || fbF5.stress || {};
  const f5Bie = fbF5.bienestar || fbF5.wellbeing || {};
  const fbF5Items = [
    ...(f5Ans.respuestas || f5Ans.responses || []),
    ...(f5Est.respuestas || f5Est.responses || []),
    ...(f5Bie.respuestas || f5Bie.responses || []),
  ];
  const gcpF5Obs = gcpObs.filter((o) => isGcpF5Observation(o));
  const fbF5Date = fbF5.completedAt || fbF5.createdAt || fbF5.date || null;
  const gcpF5Date = maxObsDate(gcpF5Obs);
  if (fbF5Items.length > 0 || gcpF5Obs.length > 0) {
    const f5Rows = [];
    const allF5Codes = new Set([...fbF5Items.map((i) => i.code), ...gcpF5Obs.map((o) => o.code)].filter(Boolean));
    allF5Codes.forEach((code) => {
      const fbItem = fbF5Items.find((i) => i.code === code);
      const gcpO = gcpF5Obs.find((o) => o.code === code);
      const fbVal = fbItem ? (fbItem.valueDisplay ?? fbItem.value ?? null) : null;
      f5Rows.push({
        label: `[${code}]`,
        fb: fbVal,
        gcp: gcpO ? (gcpO.value ?? null) : null,
      });
    });
    groups.push({ title: "F5: Bienestar Emocional", rows: f5Rows, fbDate: fbF5Date, gcpDate: gcpF5Date });
  }

  return groups;
}

/**
 * Reconstruct/patch a local Firestore form (F2..F5) from the latest GCP FHIR observations.
 * Used when an Admin clicks "Traer de FHIR" because FHIR has a more recent effective timestamp.
 */
export function buildPatchedFormFromGcp(formKey, fbProfile, gcpData) {
  const fb = fbProfile || {};
  const allGcpObs = Array.isArray(gcpData?.profile?.observations) ? gcpData.profile.observations : [];
  const obsMap = new Map();
  const displayObsMap = new Map();
  allGcpObs.forEach((o) => {
    if (!o || typeof o !== "object") return;
    const currentDate = o.effectiveDateTime || o.issued || o.date || "";
    if (o.code) {
      const existing = obsMap.get(o.code);
      const existingDate = existing?.effectiveDateTime || existing?.issued || existing?.date || "";
      if (!existing || currentDate >= existingDate) obsMap.set(o.code, o);
    }
    const disp = (typeof o.display === "string" ? o.display : (o.display ? String(o.display) : "")).trim();
    if (disp) {
      const existingDisp = displayObsMap.get(disp);
      const existingDispDate = existingDisp?.effectiveDateTime || existingDisp?.issued || existingDisp?.date || "";
      if (!existingDisp || currentDate >= existingDispDate) displayObsMap.set(disp, o);
    }
  });
  const gcpObs = Array.from(obsMap.values());
  const findGcpByDisplay = (display) => {
    const o = displayObsMap.get(display);
    return o ? (o.value ?? o.valueCode?.display ?? null) : null;
  };
  const maxObsDate = (items = []) => {
    let max = null;
    items.forEach((o) => {
      const d = o?.effectiveDateTime || o?.issued || o?.date || null;
      if (d && (!max || d > max)) max = d;
    });
    return max;
  };

  if (formKey === "hasF2") {
    const base = { ...getFbFormData(fb, "F2-PAR-Q-Physical", "f2Results", buildF2Firestore) };
    const f2GcpCodes = ["64098-7", "we360-sentadillas-30s", "we360-flexiones-max", "we360-flexibilidad", "we360:6mwt", "we360:squats-1min", "we360:pushups-1min"];
    const gcpF2Obs = gcpObs.filter((o) => f2GcpCodes.includes(o.code) || (o.code && o.code.startsWith("we360:parq")));
    const gcpDate = maxObsDate([
      ...gcpF2Obs,
      displayObsMap.get("6-Minute Walk Classification"),
      displayObsMap.get("Squats Classification"),
      displayObsMap.get("Push-ups Classification"),
      displayObsMap.get("Flexibility Classification"),
    ]) || new Date().toISOString();

    const physObs = [...(base.observaciones || base.observations || [])];
    gcpF2Obs.filter((o) => !o.code?.startsWith("we360:parq")).forEach((go) => {
      const idx = physObs.findIndex((po) => po?.code === go.code);
      if (idx >= 0) {
        const updated = { ...physObs[idx], value: go.value };
        if (updated.valueCode) {
          updated.valueCode = { ...updated.valueCode, display: String(go.value), code: String(go.value) };
        }
        physObs[idx] = updated;
      } else {
        physObs.push({ code: go.code, display: go.display || go.code, value: go.value, unit: go.unit || null });
      }
    });

    const parqList = [...(base.parqResponses || [])];
    const gcpParqItems = gcpF2Obs.filter((o) => o.code && o.code.startsWith("we360:parq"));
    gcpParqItems.forEach((go) => {
      const boolVal = go.value === true || go.value === "true";
      const idx = parqList.findIndex((p) => p?.code === go.code);
      if (idx >= 0) {
        parqList[idx] = { ...parqList[idx], value: boolVal };
      } else {
        parqList.push({ code: go.code, display: go.display || go.code, value: boolVal });
      }
    });
    const parqAlert = parqList.length > 0
      ? parqList.some((p) => p && (p.value === true || p.value === "true" || p === true || p === "true" || p.respuesta === true))
      : (base.parqAlert ?? base.parqAlerta ?? false);

    const c6mwt = findGcpByDisplay("6-Minute Walk Classification") ?? base.clasificacion6mwt ?? base.classification6mwt ?? null;
    const cSquats = findGcpByDisplay("Squats Classification") ?? base.clasificacionSentadillas ?? base.classificationSquats ?? null;
    const cPushups = findGcpByDisplay("Push-ups Classification") ?? base.clasificacionFlexiones ?? base.classificationPushups ?? null;
    const cFlex = findGcpByDisplay("Flexibility Classification") ?? base.clasificacionFlexibilidad ?? base.classificationFlexibility ?? null;

    const patched = {
      ...base,
      observaciones: physObs,
      observations: physObs,
      parqResponses: parqList,
      parqAlerta: parqAlert,
      parqAlert,
      clasificacion6mwt: c6mwt,
      classification6mwt: c6mwt,
      clasificacionSentadillas: cSquats,
      classificationSquats: cSquats,
      clasificacionFlexiones: cPushups,
      classificationPushups: cPushups,
      clasificacionFlexibilidad: cFlex,
      classificationFlexibility: cFlex,
      completedAt: gcpDate,
    };
    return { formId: "F2", questionnaire: "F2-PAR-Q-Physical", legacyKey: "f2Results", data: patched, completedAt: gcpDate };
  }

  if (formKey === "hasF3") {
    const base = { ...getFbFormData(fb, "F3-IPAQ", "f3Results", buildF3Firestore) };
    const f3GcpCodes = ["we360-met-total", "we360-ipaq-nivel", "we360-sedentarismo", "we360:ipaq-vigorous", "we360:ipaq-moderate", "we360:ipaq-walking"];
    const gcpF3Obs = gcpObs.filter((o) => f3GcpCodes.includes(o.code));
    const gcpDate = maxObsDate([
      ...gcpF3Obs,
      displayObsMap.get("Time Spent Sitting (IPAQ)"),
    ]) || new Date().toISOString();

    const obsList = [...(base.observaciones || base.observations || [])];
    ["we360-met-total", "we360-ipaq-nivel", "we360-sedentarismo"].forEach((code) => {
      const go = gcpF3Obs.find((o) => o?.code === code);
      if (!go) return;
      const idx = obsList.findIndex((o) => o?.code === code);
      if (idx >= 0) {
        obsList[idx] = { ...obsList[idx], value: go.value, valueDisplay: String(go.value) };
      } else {
        obsList.push({ code: go.code, display: go.display || go.code, value: go.value, unit: go.unit || null });
      }
    });

    const patchAct = (actObj, code) => {
      const go = gcpF3Obs.find((o) => o?.code === code);
      if (!go) return actObj;
      return { ...(actObj || { code }), metMin: go.value, metMinutes: go.value };
    };
    const vigorosa = patchAct(base.vigorosa || base.vigorous, "we360:ipaq-vigorous");
    const moderada = patchAct(base.moderada || base.moderate, "we360:ipaq-moderate");
    const caminata = patchAct(base.caminata || base.walking, "we360:ipaq-walking");

    const gcpSitting = findGcpByDisplay("Time Spent Sitting (IPAQ)");
    const rawSit = base.tiempoSentado || base.sittingTime;
    const sittingObj = gcpSitting != null
      ? { ...(typeof rawSit === "object" && rawSit ? rawSit : {}), display: gcpSitting, code: gcpSitting }
      : rawSit;

    const patched = {
      ...base,
      observaciones: obsList,
      observations: obsList,
      ...(vigorosa ? { vigorosa, vigorous: vigorosa } : {}),
      ...(moderada ? { moderada, moderate: moderada } : {}),
      ...(caminata ? { caminata, walking: caminata } : {}),
      ...(sittingObj ? { tiempoSentado: sittingObj, sittingTime: sittingObj } : {}),
      completedAt: gcpDate,
    };
    return { formId: "F3", questionnaire: "F3-IPAQ", legacyKey: "f3Results", data: patched, completedAt: gcpDate };
  }

  if (formKey === "hasF4") {
    const base = { ...getFbFormData(fb, "F4-Nutrition", "f4Results", buildF4Firestore) };
    const gcpF4Obs = gcpObs.filter((o) => o.code && (o.code.startsWith("we360:food-") || o.code.startsWith("we360:behav-") || o.code.startsWith("we360:fruit") || o.code.startsWith("we360:vegetable") || o.code.startsWith("we360:processed") || o.code === "we360:hydration"));
    const gcpDate = maxObsDate([
      ...gcpF4Obs,
      displayObsMap.get("Diet Quality Score"),
      displayObsMap.get("Behavior Score"),
      displayObsMap.get("Hydration Status"),
    ]) || new Date().toISOString();

    const patchItems = (list = [], matchFn) => {
      const updated = list.map((item) => {
        if (!item || typeof item !== "object") return item;
        const go = gcpF4Obs.find((o) => o?.code === item.code);
        if (!go) return item;
        const prevFreq = item.frecuencia || item.frequency || {};
        const nextFreq = { ...(typeof prevFreq === "object" ? prevFreq : {}), display: go.value };
        return { ...item, frecuencia: nextFreq, frequency: nextFreq };
      });
      gcpF4Obs.filter(matchFn).forEach((go) => {
        if (!updated.some((it) => it && it.code === go.code)) {
          updated.push({
            code: go.code,
            display: go.display || go.code,
            frecuencia: { display: go.value },
            frequency: { display: go.value },
          });
        }
      });
      return updated;
    };

    const consumo = patchItems(
      base.consumo || base.foodIntake || [],
      (o) => o.code && (o.code.startsWith("we360:food-") || o.code.startsWith("we360:fruit") || o.code.startsWith("we360:vegetable") || o.code.startsWith("we360:processed"))
    );
    const conductas = patchItems(
      base.conductas || base.behaviors || [],
      (o) => o.code && o.code.startsWith("we360:behav-")
    );
    const gcpDietScore = findGcpByDisplay("Diet Quality Score");
    const gcpBehavScore = findGcpByDisplay("Behavior Score");
    const gcpHydration = findGcpByDisplay("Hydration Status");
    const rawHyd = base.hidratacion || base.hydration;
    const hydObj = gcpHydration != null
      ? { ...(typeof rawHyd === "object" && rawHyd ? rawHyd : {}), display: gcpHydration }
      : rawHyd;

    const patched = {
      ...base,
      consumo,
      foodIntake: consumo,
      conductas,
      behaviors: conductas,
      ...(gcpDietScore != null ? { scoreCalidadDieta: gcpDietScore, dietQualityScore: gcpDietScore } : {}),
      ...(gcpBehavScore != null ? { scoreConductas: gcpBehavScore, behaviorScore: gcpBehavScore } : {}),
      ...(hydObj ? { hidratacion: hydObj, hydration: hydObj } : {}),
      completedAt: gcpDate,
    };
    return { formId: "F4", questionnaire: "F4-Nutrition", legacyKey: "f4Results", data: patched, completedAt: gcpDate };
  }

  if (formKey === "hasF5") {
    const base = { ...getFbFormData(fb, "F5-Mental-Health", "f5Results", buildF5Firestore) };
    const gcpF5Obs = gcpObs.filter((o) => isGcpF5Observation(o));
    const gcpDate = maxObsDate(gcpF5Obs) || new Date().toISOString();

    const gadValMap = { "Not at all": 0, "Several days": 1, "More than half the days": 2, "Nearly every day": 3 };
    const gadValMapES = { "Not at all": "Ningún día", "Several days": "Varios días", "More than half the days": "Más de la mitad de los días", "Nearly every day": "Casi todos los días" };
    const stressValMap = { Never: 0, Rarely: 1, Sometimes: 2, Frequently: 3, Always: 4 };
    const stressValMapES = { Never: "Nunca", Rarely: "Rara vez", Sometimes: "A veces", Frequently: "Frecuentemente", Always: "Siempre" };

    const F5_GAD_DEFS = [
      { code: "we360:gad5-1", display: "Feeling nervous or anxious", displayES: "Sentirse nervioso/a o ansioso/a" },
      { code: "we360:gad5-2", display: "Difficulty relaxing", displayES: "Dificultad para relajarse" },
      { code: "we360:gad5-3", display: "Repetitive thoughts or constant worrying", displayES: "Pensamientos repetitivos o preocupación constante" },
      { code: "we360:gad5-4", display: "Easily irritable or upset", displayES: "Fácilmente irritable o molesto/a" },
      { code: "we360:gad5-5", display: "Difficulty sleeping or staying asleep", displayES: "Dificultad para dormir o mantener el sueño" },
    ];
    const F5_STRESS_DEFS = [
      { code: "we360:stress-1", display: "Daily burden feels excessive", displayES: "Siento que la carga diaria me supera" },
      { code: "we360:stress-2", display: "Difficulty disconnecting after work", displayES: "Me cuesta desconectarme después del trabajo" },
      { code: "we360:stress-3", display: "Emotionally drained at end of day", displayES: "Me siento emocionalmente agotado/a al final del día" },
      { code: "we360:stress-4", display: "Feeling frustrated or demotivated", displayES: "Me siento frustrado/a o desmotivado/a" },
      { code: "we360:stress-5", display: "No time for self or rest", displayES: "Siento que no tengo tiempo para mí o para descansar" },
    ];
    const F5_WB_DEFS = [
      { code: "we360:wb-1", display: "Sense of purpose or value", displayES: "Siento que lo que hago tiene propósito o valor" },
      { code: "we360:wb-2", display: "Feeling emotionally supported", displayES: "Me siento apoyado/a emocionalmente" },
      { code: "we360:wb-3", display: "Satisfied with work-life balance", displayES: "Estoy satisfecho/a con mi equilibrio vida-trabajo" },
      { code: "we360:wb-4", display: "Moments to reconnect and rest", displayES: "Tengo momentos para reconectarme y descansar" },
      { code: "we360:wb-5", display: "Feeling gratitude or enjoying small moments", displayES: "Siento gratitud o disfruto pequeños momentos" },
    ];

    const patchSec = (sec = {}, itemDefs = [], valMap = {}, valMapES = {}, sectionMeta = {}) => {
      const list = Array.isArray(sec.respuestas) && sec.respuestas.length > 0
        ? sec.respuestas
        : (Array.isArray(sec.responses) ? sec.responses : []);
      const byCode = new Map();
      list.forEach((it) => { if (it && it.code) byCode.set(it.code, it); });

      let hasGcpUpdates = false;
      const updatedList = itemDefs
        .map((def) => {
          const existing = byCode.get(def.code);
          const go = gcpF5Obs.find((o) => o?.code === def.code);
          if (!go) return existing || null;
          hasGcpUpdates = true;
          const numVal = valMap[go.value] !== undefined ? valMap[go.value] : (typeof go.value === "number" ? go.value : (existing?.value ?? 0));
          return {
            ...(existing || {}),
            code: def.code,
            display: go.display || existing?.display || def.display,
            displayES: existing?.displayES || def.displayES,
            value: numVal,
            valueDisplay: String(go.value),
            valueDisplayES: valMapES[go.value] || existing?.valueDisplayES || String(go.value),
          };
        })
        .filter(Boolean);

      const computedScore = updatedList.reduce((acc, it) => acc + (typeof it.value === "number" ? it.value : 0), 0);

      const gcpSecObs = gcpF5Obs.find((o) => o && (o.code === sectionMeta.code || (sec.code && o.code === sec.code)));
      const gcpSecScore = gcpSecObs != null && (typeof gcpSecObs.value === "number" || (!isNaN(Number(gcpSecObs.value)) && gcpSecObs.value !== ""))
        ? Number(gcpSecObs.value)
        : null;

      const finalScore = hasGcpUpdates
        ? computedScore
        : (gcpSecScore != null ? gcpSecScore : (sec.score != null ? sec.score : computedScore));

      return {
        code: sec.code || sectionMeta.code,
        display: sec.display || sectionMeta.display,
        displayES: sec.displayES || sectionMeta.displayES,
        ...sec,
        score: finalScore,
        respuestas: updatedList,
        responses: updatedList,
      };
    };

    const ansiedad = patchSec(
      base.ansiedad || base.anxiety || {},
      F5_GAD_DEFS,
      gadValMap,
      gadValMapES,
      { code: "we360:gad5", display: "Anxiety (GAD-5)", displayES: "Ansiedad (GAD-5)" }
    );
    const estres = patchSec(
      base.estres || base.stress || {},
      F5_STRESS_DEFS,
      stressValMap,
      stressValMapES,
      { code: "we360:stress", display: "Stress / Burnout", displayES: "Estrés / Burnout" }
    );
    const bienestar = patchSec(
      base.bienestar || base.wellbeing || {},
      F5_WB_DEFS,
      stressValMap,
      stressValMapES,
      { code: "we360:wellbeing", display: "Subjective well-being", displayES: "Bienestar subjetivo" }
    );

    const patched = {
      ...base,
      ansiedad,
      anxiety: ansiedad,
      estres,
      stress: estres,
      bienestar,
      wellbeing: bienestar,
      completedAt: gcpDate,
    };
    return { formId: "F5", questionnaire: "F5-Mental-Health", legacyKey: "f5Results", data: patched, completedAt: gcpDate };
  }

  return null;
}

function getRowStatus(fb, gcp) {
  const fbStr = fb == null ? "-" : String(fb);
  const gcpStr = gcp == null ? "-" : String(gcp);
  if (fbStr === "-" && gcpStr === "-") return "both_empty";
  if (fbStr === gcpStr) return "match";
  if (gcpStr === "-" || gcpStr === "null") return "missing_gcp";
  if (fbStr === "-" || fbStr === "null") return "missing_fb";
  return "mismatch";
}

const GROUP_TO_FORM_KEY = {
  "F2: Estado Físico": "hasF2",
  "F3: Actividad Física": "hasF3",
  "F4: Nutrición": "hasF4",
  "F5: Bienestar Emocional": "hasF5",
};

function fmtShortTs(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString("es-CO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return null;
  }
}

function DetailCompareView({
  fbProfile,
  gcpData,
  fallbackEmail = null,
  onSyncForm = null,
  onPullFormFromGcp = null,
  syncingForms = {},
  syncResults = {},
}) {
  const groups = extractDetailedComparison(fbProfile, gcpData, fallbackEmail);

  const statusIcon = (s) => {
    if (s === "match") return <CheckCircle2 size={12} style={{ color: "#16A34A" }} />;
    if (s === "mismatch") return <AlertCircle size={12} style={{ color: "#DC2626" }} />;
    if (s === "missing_gcp") return <MinusCircle size={12} style={{ color: "#D97706" }} />;
    if (s === "missing_fb") return <MinusCircle size={12} style={{ color: "#7C3AED" }} />;
    return <MinusCircle size={12} style={{ color: "#CBD5E1" }} />;
  };

  const statusBg = (s) => {
    if (s === "match") return "#F0FDF4";
    if (s === "mismatch") return "#FEF2F2";
    if (s === "missing_gcp") return "#FFFBEB";
    if (s === "missing_fb") return "#F5F3FF";
    return "#F8FAFC";
  };

  // Stats
  let totalRows = 0, matches = 0, mismatches = 0, missingGcp = 0, missingFb = 0;
  groups.forEach((g) => g.rows.forEach((r) => {
    const s = getRowStatus(r.fb, r.gcp);
    if (s !== "both_empty") {
      totalRows++;
      if (s === "match") matches++;
      if (s === "mismatch") mismatches++;
      if (s === "missing_gcp") missingGcp++;
      if (s === "missing_fb") missingFb++;
    }
  }));

  return (
    <div className="overflow-y-auto" style={{ maxHeight: "calc(85vh - 100px)" }}>
      {/* Stats bar */}
      <div className="flex gap-3 px-5 py-3 sticky top-0 z-10" style={{ background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: "#DCFCE7" }}>
          <CheckCircle2 size={12} style={{ color: "#16A34A" }} />
          <span className="text-[10px] font-bold" style={{ color: "#166534" }}>{matches} iguales</span>
        </div>
        {mismatches > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: "#FEF2F2" }}>
            <AlertCircle size={12} style={{ color: "#DC2626" }} />
            <span className="text-[10px] font-bold" style={{ color: "#991B1B" }}>{mismatches} diferentes</span>
          </div>
        )}
        {missingGcp > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: "#FFFBEB" }}>
            <MinusCircle size={12} style={{ color: "#D97706" }} />
            <span className="text-[10px] font-bold" style={{ color: "#92400E" }}>{missingGcp} falta GCP</span>
          </div>
        )}
        {missingFb > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: "#F5F3FF" }}>
            <MinusCircle size={12} style={{ color: "#7C3AED" }} />
            <span className="text-[10px] font-bold" style={{ color: "#5B21B6" }}>{missingFb} falta FB</span>
          </div>
        )}
        <span className="ml-auto text-[10px] font-mono self-center" style={{ color: "#94A3B8" }}>
          {totalRows > 0 ? `${matches}/${totalRows} sync` : "Sin datos"}
        </span>
      </div>

      {groups.map((group, gi) => {
        const formKey = GROUP_TO_FORM_KEY[group.title];
        const groupHasDiff = group.rows.some((r) => {
          const s = getRowStatus(r.fb, r.gcp);
          return s === "mismatch" || s === "missing_gcp" || s === "missing_fb";
        });
        const hasMismatchOrMissingFb = group.rows.some((r) => {
          const s = getRowStatus(r.fb, r.gcp);
          return s === "mismatch" || s === "missing_fb";
        });
        const gcpIsNewer = Boolean(
          hasMismatchOrMissingFb &&
          group.gcpDate &&
          (!group.fbDate || group.gcpDate >= group.fbDate)
        );
        const isSyncing = formKey ? syncingForms[formKey] : false;
        const syncResult = formKey ? syncResults[formKey] : null;
        const fbTsLabel = fmtShortTs(group.fbDate);
        const gcpTsLabel = fmtShortTs(group.gcpDate);

        return (
          <div key={gi} data-testid={`detail-group-${gi}`}>
            {/* Group header */}
            <div className="px-5 py-2 flex flex-wrap items-center justify-between gap-2" style={{ background: "#F1F5F9", borderBottom: "1px solid #E2E8F0" }}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "#475569" }}>{group.title}</span>
                {(fbTsLabel || gcpTsLabel) && (
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: "#E2E8F0", color: "#475569" }}>
                    {fbTsLabel ? `FB: ${fbTsLabel}` : "FB: s/f"}
                    {" · "}
                    {gcpTsLabel ? `FHIR: ${gcpTsLabel}` : "FHIR: s/f"}
                  </span>
                )}
                {groupHasDiff && gcpIsNewer && !syncResult?.verified && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: "#DCFCE7", color: "#166534" }}>
                    FHIR más reciente
                  </span>
                )}
              </div>
              {formKey && onSyncForm && (groupHasDiff || syncResult) && (
                <div className="flex items-center gap-1.5">
                  {syncResult?.verified ? (
                    <span className="flex items-center gap-1 text-[10px] font-bold" style={{ color: "#16A34A" }}>
                      <CheckCircle2 size={11} /> Sincronizado
                    </span>
                  ) : syncResult?.ok && syncResult?.verifying ? (
                    <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: "#EA580C" }}>
                      <Loader2 size={10} className="animate-spin" /> Verificando...
                    </span>
                  ) : groupHasDiff && gcpIsNewer ? (
                    <>
                      {onPullFormFromGcp && (
                        <button
                          onClick={() => onPullFormFromGcp(formKey)}
                          disabled={isSyncing}
                          title="Actualizar Firestore local con las observaciones más recientes de FHIR"
                          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold cursor-pointer transition-all hover:opacity-90"
                          style={{ background: "#059669", color: "white", border: "none", opacity: isSyncing ? 0.6 : 1 }}
                          data-testid={`detail-pull-btn-${formKey}`}
                        >
                          {isSyncing ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                          Traer de FHIR
                        </button>
                      )}
                      <button
                        onClick={() => onSyncForm(formKey, { forceTimestamp: true })}
                        disabled={isSyncing}
                        title="Estampar fecha actual y forzar que los datos locales prevalezcan en FHIR"
                        className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold cursor-pointer transition-all hover:opacity-90"
                        style={{ background: "#EA580C", color: "white", border: "none", opacity: isSyncing ? 0.6 : 1 }}
                        data-testid={`detail-sync-btn-${formKey}`}
                      >
                        {isSyncing ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
                        Forzar en FHIR
                      </button>
                    </>
                  ) : groupHasDiff ? (
                    <button
                      onClick={() => onSyncForm(formKey, { forceTimestamp: false })}
                      disabled={isSyncing}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold cursor-pointer transition-all hover:opacity-90"
                      style={{ background: "#EA580C", color: "white", border: "none", opacity: isSyncing ? 0.6 : 1 }}
                      data-testid={`detail-sync-btn-${formKey}`}
                    >
                      {isSyncing ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
                      Sincronizar con FHIR
                    </button>
                  ) : null}
                </div>
              )}
            </div>
            {/* Rows */}
            <table className="w-full text-xs">
              <tbody>
                {group.rows.map((row, ri) => {
                  const s = getRowStatus(row.fb, row.gcp);
                  if (s === "both_empty") return null;
                  const fbStr = row.fb == null ? "-" : String(row.fb);
                  const gcpStr = row.gcp == null ? "-" : String(row.gcp);
                  return (
                    <tr key={ri} style={{ background: statusBg(s), borderBottom: "1px solid #F1F5F9" }}>
                      <td className="px-5 py-1.5 font-medium" style={{ color: "#1E293B", width: "28%" }}>{row.label}</td>
                      <td className="px-3 py-1.5 font-mono" style={{ color: fbStr === "-" ? "#CBD5E1" : "#1E293B", width: "30%" }}>
                        {fbStr}{row.unit && fbStr !== "-" ? ` ${row.unit}` : ""}
                      </td>
                      <td className="px-3 py-1.5 font-mono" style={{ color: gcpStr === "-" ? "#CBD5E1" : "#1E293B", width: "30%" }}>
                        {gcpStr}{row.unit && gcpStr !== "-" ? ` ${row.unit}` : ""}
                      </td>
                      <td className="px-3 py-1.5 text-center" style={{ width: "12%" }}>{statusIcon(s)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Legend */}
      <div className="flex gap-4 px-5 py-3 text-[10px]" style={{ background: "#F8FAFC", borderTop: "1px solid #E2E8F0" }}>
        <span style={{ color: "#16A34A" }}><CheckCircle2 size={10} className="inline mr-0.5" /> Igual</span>
        <span style={{ color: "#DC2626" }}><AlertCircle size={10} className="inline mr-0.5" /> Diferente</span>
        <span style={{ color: "#D97706" }}><MinusCircle size={10} className="inline mr-0.5" /> Falta en GCP</span>
        <span style={{ color: "#7C3AED" }}><MinusCircle size={10} className="inline mr-0.5" /> Falta en Firebase</span>
      </div>
    </div>
  );
}

// ── Raw JSON side-by-side (no diff, just raw view) ──────────
function RawSideBySide({ firebaseJson, gcpJson }) {
  const leftLines = JSON.stringify(firebaseJson || {}, null, 2).split("\n");
  const rightLines = JSON.stringify(gcpJson || {}, null, 2).split("\n");

  return (
    <div>
      <div className="flex items-center gap-3 px-4 py-2" style={{ background: "#1E293B" }}>
        <span className="text-[10px] font-mono" style={{ color: "#94A3B8" }}>{leftLines.length} líneas Firebase</span>
        <span className="text-[10px] font-mono" style={{ color: "#94A3B8" }}>|</span>
        <span className="text-[10px] font-mono" style={{ color: "#94A3B8" }}>{rightLines.length} líneas GCP</span>
      </div>
      <div className="flex overflow-auto" style={{ maxHeight: "calc(85vh - 200px)", background: "#0F172A" }}>
        {/* Left: Firebase */}
        <div className="flex-1 min-w-0" style={{ borderRight: "1px solid #334155" }}>
          <div className="sticky top-0 px-3 py-1.5 text-[10px] font-bold" style={{ background: "#1E3A5F", color: "#60A5FA" }}>
            Firebase / Firestore (Local)
          </div>
          <pre className="text-[11px] font-mono leading-5" style={{ margin: 0 }}>
            {leftLines.map((line, i) => (
              <div key={`l-${i}`} className="flex">
                <span className="inline-block text-right px-2 select-none shrink-0" style={{ width: 36, color: "#475569", background: "#1E293B" }}>{i + 1}</span>
                <span className="px-2 whitespace-pre" style={{ color: "#94A3B8" }}>{line || " "}</span>
              </div>
            ))}
          </pre>
        </div>
        {/* Right: GCP */}
        <div className="flex-1 min-w-0">
          <div className="sticky top-0 px-3 py-1.5 text-[10px] font-bold" style={{ background: "#1A3D2E", color: "#4ADE80" }}>
            Google Cloud FHIR Store (Remoto)
          </div>
          <pre className="text-[11px] font-mono leading-5" style={{ margin: 0 }}>
            {rightLines.map((line, i) => (
              <div key={`r-${i}`} className="flex">
                <span className="inline-block text-right px-2 select-none shrink-0" style={{ width: 36, color: "#475569", background: "#1E293B" }}>{i + 1}</span>
                <span className="px-2 whitespace-pre" style={{ color: "#94A3B8" }}>{line || " "}</span>
              </div>
            ))}
          </pre>
        </div>
      </div>
    </div>
  );
}

// ── SyncModal ───────────────────────────────────────────────
export function SyncModal({ user, onClose, onUserUpdated }) {
  const [loading, setLoading] = useState(true);
  const [gcpData, setGcpData] = useState(null);
  const [gcpError, setGcpError] = useState(null);
  const [tab, setTab] = useState("detail"); // summary | detail | raw
  const [syncingForms, setSyncingForms] = useState({});
  const [syncResults, setSyncResults] = useState({});
  const [outboxStats, setOutboxStats] = useState({ total: 0, synced: 0, pending: 0, loading: true });
  const [flushingOutbox, setFlushingOutbox] = useState(false);
  const [flushFeedback, setFlushFeedback] = useState(null);
  const [effectiveProfile, setEffectiveProfile] = useState(user.profile || {});

  // Extract F1 fields from profile for direct sync to GCP
  const extractF1ForSync = (profile) => {
    if (!profile) return null;
    const f1Keys = [
      "email", "whatsapp", "firstName", "lastName", "birthDate", "sex",
      "maritalStatus", "educationLevel", "profession", "occupation", "residence",
      "vitalSigns", "conditions", "usesMedication", "medications",
      "consent", "createdAt", "completedAt",
      // FHIR / Spanish key fallbacks
      "name", "gender", "observations",
      "nombres", "apellidos", "fechaNacimiento", "sexo",
      "estadoCivil", "nivelEducativo", "profesion", "ocupacion", "residencia",
      "signos", "condiciones", "usaMedicamentos", "medicamentos",
      "consentimiento",
    ];
    const result = {};
    for (const key of f1Keys) {
      if (profile[key] !== undefined) result[key] = profile[key];
    }
    return Object.keys(result).length > 0 ? result : null;
  };

  const getNonEmptyFormData = (p, questionnaireId, legacyKey, builderFn) => {
    const obj = getFbFormData(p, questionnaireId, legacyKey, builderFn);
    return obj && Object.keys(obj).length > 0 ? obj : null;
  };

  const FORM_SYNC_MAP = {
    hasF1: { formId: "F1", getData: (p) => extractF1ForSync(p) },
    hasF2: { formId: "F2", getData: (p) => getNonEmptyFormData(p, "F2-PAR-Q-Physical", "f2Results", buildF2Firestore) },
    hasF3: { formId: "F3", getData: (p) => getNonEmptyFormData(p, "F3-IPAQ", "f3Results", buildF3Firestore) },
    hasF4: { formId: "F4", getData: (p) => getNonEmptyFormData(p, "F4-Nutrition", "f4Results", buildF4Firestore) },
    hasF5: { formId: "F5", getData: (p) => getNonEmptyFormData(p, "F5-Mental-Health", "f5Results", buildF5Firestore) },
  };

  const email = user.email || effectiveProfile?.email;
  const fbData = extractFirebaseF1(effectiveProfile);
  const syncInfo = getUserFhirSyncInfo({ ...user, profile: effectiveProfile });

  const loadLocalOutboxStats = useCallback(async (autoFlush = false) => {
    if (!user?.uid) {
      setOutboxStats({ total: 0, synced: 0, pending: 0, loading: false });
      return;
    }
    try {
      let mSnap = await getDocs(collection(db, "users", user.uid, "measurements"));
      let total = 0;
      let synced = 0;
      let pending = 0;
      mSnap.forEach((docSnap) => {
        total++;
        const d = docSnap.data() || {};
        if (d.syncStatus === "PENDING_FHIR" || d.syncStatus === "FAILED") pending++;
        else synced++;
      });
      if (autoFlush && pending > 0 && email) {
        const flushRes = await flushPendingMeasurementsToGCP(user.uid, email);
        if (flushRes.flushedCount > 0) {
          mSnap = await getDocs(collection(db, "users", user.uid, "measurements"));
          total = 0;
          synced = 0;
          pending = 0;
          mSnap.forEach((docSnap) => {
            total++;
            const d = docSnap.data() || {};
            if (d.syncStatus === "PENDING_FHIR" || d.syncStatus === "FAILED") pending++;
            else synced++;
          });
        }
      }
      setOutboxStats({ total, synced, pending, loading: false });
    } catch {
      setOutboxStats({ total: 0, synced: 0, pending: 0, loading: false });
    }
  }, [user?.uid, email]);

  useEffect(() => {
    loadLocalOutboxStats(true);
    if (!email) { setLoading(false); return; }
    queryBackend("/api/patients/everything", email).then((res) => {
      if (res.ok) {
        setGcpData(res.data);
        let nextProfile = effectiveProfile ? { ...effectiveProfile } : {};
        let profileChanged = false;
        const fsUpdates = {};

        // Clean up any legacy corrupted field pendingFhirundefined
        if ("pendingFhirundefined" in user || (nextProfile && "pendingFhirundefined" in nextProfile)) {
          fsUpdates.pendingFhirundefined = deleteField();
          fsUpdates["profile.pendingFhirundefined"] = deleteField();
        }

        // Auto-heal medications from GCP FHIR into Firestore if Firebase has 0 FB and GCP has >0 GCP
        // (unless the user explicitly confirmed clearing all medications via medicationsConfirmedEmpty)
        const gcpMeds = dedupGcpMedications(res.data?.profile?.medications || []);
        const currentFbMeds = (nextProfile?.medications?.length > 0 ? nextProfile.medications : nextProfile?.medicamentos) || [];
        if (currentFbMeds.length === 0 && gcpMeds.length > 0 && !nextProfile?.medicationsConfirmedEmpty) {
          const healedMeds = gcpMeds.map(parseGcpMedForFirestore).filter(Boolean);
          if (healedMeds.length > 0) {
            nextProfile = {
              ...nextProfile,
              medications: healedMeds,
              usesMedication: true,
            };
            fsUpdates["profile.medications"] = healedMeds;
            fsUpdates["profile.usesMedication"] = true;
            profileChanged = true;
          }
        }

        // Auto-heal clinical forms (F2..F5) if GCP FHIR has items that are missing in Firebase (missing_fb)
        // or if existingOther contains corrupted entries without questionnaire
        const gcpExt = extractGcpData(res.data);
        const groupsCheck = extractDetailedComparison(nextProfile, res.data, email);
        const titleMap = {
          hasF2: "F2: Estado Físico",
          hasF3: "F3: Actividad Física",
          hasF4: "F4: Nutrición",
          hasF5: "F5: Bienestar Emocional",
        };
        let workingOther = Array.isArray(nextProfile?.other)
          ? nextProfile.other.filter((o) => o && typeof o === "object" && Boolean(o.questionnaire))
          : [];
        if (Array.isArray(nextProfile?.other) && workingOther.length !== nextProfile.other.length) {
          profileChanged = true;
        }

        for (const fKey of ["hasF2", "hasF3", "hasF4", "hasF5"]) {
          if (!gcpExt?.[fKey]) continue;
          const grp = groupsCheck.find((g) => g.title === titleMap[fKey]);
          const hasMissingFbRows = grp && grp.rows.some((r) => getRowStatus(r.fb, r.gcp) === "missing_fb");
          const hasMissingGcpRows = grp && grp.rows.some((r) => getRowStatus(r.fb, r.gcp) === "missing_gcp");
          if (hasMissingFbRows && !hasMissingGcpRows) {
            const patched = buildPatchedFormFromGcp(fKey, nextProfile, res.data);
            if (patched) {
              const wrappedEntry = {
                resourceType: "QuestionnaireResponse",
                questionnaire: patched.questionnaire,
                date: patched.completedAt,
                completedAt: patched.completedAt,
                data: patched.data,
              };
              const idx = workingOther.findIndex((o) => o && o.questionnaire === patched.questionnaire);
              if (idx >= 0) workingOther[idx] = wrappedEntry;
              else workingOther.push(wrappedEntry);
              nextProfile = {
                ...nextProfile,
                other: workingOther,
                [patched.legacyKey]: patched.data,
                [`pendingFhir${patched.formId}`]: false,
              };
              fsUpdates[`profile.${patched.legacyKey}`] = patched.data;
              fsUpdates[`pendingFhir${patched.formId}`] = false;
              fsUpdates[`profile.pendingFhir${patched.formId}`] = false;
              profileChanged = true;
            }
          }
        }

        if (profileChanged) {
          nextProfile = { ...nextProfile, other: workingOther };
          fsUpdates["profile.other"] = workingOther;
          setEffectiveProfile(nextProfile);
          if (onUserUpdated && user?.uid) {
            onUserUpdated(user.uid, { profile: nextProfile });
          }
        }
        if (user?.uid && Object.keys(fsUpdates).length > 0) {
          try {
            Promise.resolve(updateDoc(doc(db, "users", user.uid), fsUpdates)).catch(() => {});
          } catch {}
        }
      } else {
        setGcpError(res.error || `Status ${res.status}`);
      }
      setLoading(false);
    });
  }, [email, loadLocalOutboxStats]);

  const gcpExtracted = gcpData ? extractGcpData(gcpData) : null;
  const detailedGroups = gcpData ? extractDetailedComparison(effectiveProfile, gcpData, email) : [];
  const formGroupTitleMap = {
    hasF2: "F2: Estado Físico",
    hasF3: "F3: Actividad Física",
    hasF4: "F4: Nutrición",
    hasF5: "F5: Bienestar Emocional",
  };
  const baseComparison = compareFields(fbData, gcpExtracted);
  const comparison = baseComparison.map((c) => {
    const groupTitle = formGroupTitleMap[c.key];
    if (groupTitle && gcpData) {
      const grp = detailedGroups.find((g) => g.title === groupTitle);
      if (grp) {
        const hasMismatch = grp.rows.some((r) => getRowStatus(r.fb, r.gcp) === "mismatch");
        const hasMissingGcp = grp.rows.some((r) => getRowStatus(r.fb, r.gcp) === "missing_gcp");
        const hasMissingFb = grp.rows.some((r) => getRowStatus(r.fb, r.gcp) === "missing_fb");
        if (hasMismatch) {
          return { ...c, gcpVal: "Diferente (ver Detalle)", status: "mismatch" };
        }
        if (hasMissingGcp && c.gcpVal === "true") {
          return { ...c, gcpVal: "Incompleto (ver Detalle)", status: "missing_gcp" };
        }
        if (hasMissingFb && c.fbVal === "true") {
          return { ...c, fbVal: "Incompleto (ver Detalle)", status: "mismatch" };
        }
      }
    }
    return c;
  });
  const matchCount = comparison.filter((c) => c.status === "match").length;
  const mismatchCount = comparison.filter((c) => c.status === "mismatch").length;
  const missingGcp = comparison.filter((c) => c.status === "missing_gcp").length;
  const total = comparison.filter((c) => c.status !== "both_empty").length;

  // Compute live effective status badge so top pill reflects actual GCP comparison
  const hasGcpDrift = !loading && !gcpError && (missingGcp > 0 || mismatchCount > 0);
  const effectiveSyncBadge = hasGcpDrift
    ? {
        status: "PENDING_FHIR",
        label: `Pendiente en FHIR (${missingGcp + mismatchCount})`,
        bg: "#FFFBEB",
        color: "#B45309",
        border: "#FDE68A",
      }
    : syncInfo;

  // Auto-mark verified in Firestore if GCP matches all local fields and no outbox items are pending
  useEffect(() => {
    if (
      !loading &&
      !gcpError &&
      !outboxStats.loading &&
      total > 0 &&
      mismatchCount === 0 &&
      missingGcp === 0 &&
      outboxStats.pending === 0 &&
      syncInfo.status === "UNVERIFIED" &&
      user?.uid
    ) {
      const nowIso = new Date().toISOString();
      updateDoc(doc(db, "users", user.uid), {
        fhirSyncStatus: "SYNCED",
        fhirReconciled: true,
        lastFhirSyncAt: nowIso,
      }).catch(() => {});
      if (onUserUpdated) {
        onUserUpdated(user.uid, {
          fhirSyncStatus: "SYNCED",
          fhirReconciled: true,
          lastFhirSyncAt: nowIso,
        });
      }
    }
  }, [loading, gcpError, outboxStats.loading, outboxStats.pending, total, mismatchCount, missingGcp, syncInfo.status, user?.uid, onUserUpdated]);

  const statusIcon = (s) => {
    if (s === "match") return <CheckCircle2 size={13} style={{ color: "#16A34A" }} />;
    if (s === "mismatch") return <AlertCircle size={13} style={{ color: "#DC2626" }} />;
    if (s === "missing_gcp") return <MinusCircle size={13} style={{ color: "#D97706" }} />;
    if (s === "missing_fb") return <MinusCircle size={13} style={{ color: "#7C3AED" }} />;
    return <MinusCircle size={13} style={{ color: "#CBD5E1" }} />;
  };

  const statusColor = (s) => {
    if (s === "match") return { bg: "#F0FDF4", color: "#166534" };
    if (s === "mismatch") return { bg: "#FEF2F2", color: "#991B1B" };
    if (s === "missing_gcp") return { bg: "#FFFBEB", color: "#92400E" };
    if (s === "missing_fb") return { bg: "#F5F3FF", color: "#5B21B6" };
    return { bg: "#F8FAFC", color: "#94A3B8" };
  };

  const hasData = !loading && !gcpError && email;

  // ── Form sync handlers ──
  // Check if vital signs or F1 demographic fields differ (FB vs GCP)
  const vitalSignsComp = comparison.find((c) => c.key === "vitalSignsCount");
  const hasVitalSignsDiff = vitalSignsComp && vitalSignsComp.status !== "match" && vitalSignsComp.status !== "both_empty";
  const hasF1Diff = comparison.some(
    (c) =>
      ["name", "birthDate", "sex", "weight", "height", "bpSys", "bpDia", "glucose", "spo2", "vitalSignsCount", "conditions", "medications"].includes(c.key) &&
      (c.status === "missing_gcp" || c.status === "mismatch")
  );
  
  const isFormGcpNewer = (formKey) => {
    const groupTitle = formGroupTitleMap[formKey];
    if (!groupTitle) return false;
    const grp = detailedGroups.find((g) => g.title === groupTitle);
    if (!grp) return false;
    return Boolean(grp.gcpDate && (!grp.fbDate || grp.gcpDate >= grp.fbDate));
  };

  // Allow automatic batch syncing F1..F5 if missing in GCP OR if Firebase is strictly newer than GCP
  const syncableForms = comparison
    .filter((c) => {
      if (!FORM_SYNC_MAP[c.key]) return false;
      if (syncResults[c.key]?.ok) return false; // Already synced in this session
      
      // Special case for F1: allow sync if demographics or vital signs differ
      if (c.key === "hasF1" && (c.fbVal === "true" || hasF1Diff)) {
        return c.gcpVal === "false" || c.gcpVal === "-" || hasVitalSignsDiff || hasF1Diff;
      }
      
      // For F2..F5: allow automatic batch sync if missing in GCP, or if mismatch AND Firebase is newer than GCP
      return c.fbVal === "true" && (c.status === "missing_gcp" || (c.status === "mismatch" && !isFormGcpNewer(c.key)));
    })
    .map((c) => c.key);

  const verifySync = (formKey, profileOverride = null) => {
    let attempts = 0;
    const profToUse = profileOverride || effectiveProfile;
    const poll = async () => {
      attempts++;
      const res = await queryBackend("/api/patients/everything", email);
      if (res.ok) {
        setGcpData(res.data);
        const newGcp = extractGcpData(res.data);
        const groupTitle = formGroupTitleMap[formKey];
        let detailSynced = true;
        if (groupTitle) {
          const newGroups = extractDetailedComparison(profToUse, res.data, email);
          const grp = newGroups.find((g) => g.title === groupTitle);
          if (grp) {
            detailSynced = !grp.rows.some((r) => {
              const s = getRowStatus(r.fb, r.gcp);
              return s === "mismatch" || s === "missing_gcp";
            });
          }
        }
        if (newGcp && newGcp[formKey] && detailSynced) {
          setSyncResults((prev) => ({ ...prev, [formKey]: { ok: true, verified: true, verifying: false } }));
          return;
        }
      }
      if (attempts < 6) {
        setTimeout(poll, 4000);
      } else {
        // Max retries — stop but keep "Enviado"
        setSyncResults((prev) => ({ ...prev, [formKey]: { ...prev[formKey], verifying: false } }));
      }
    };
    setTimeout(poll, 3000);
  };

  const handleSyncForm = async (formKey, options = {}) => {
    const { formId, getData } = FORM_SYNC_MAP[formKey];
    const formData = getData(effectiveProfile);
    if (!formData || !email) return;
    setSyncingForms((prev) => ({ ...prev, [formKey]: true }));
    const result = await adminResyncForm(formId, email, formData, user.uid, options);
    setSyncingForms((prev) => ({ ...prev, [formKey]: false }));
    if (result.ok) {
      let nextProfile = effectiveProfile;
      if (result.forcedCompletedAt) {
        const qMap = { F2: "F2-PAR-Q-Physical", F3: "F3-IPAQ", F4: "F4-Nutrition", F5: "F5-Mental-Health" };
        const lMap = { F2: "f2Results", F3: "f3Results", F4: "f4Results", F5: "f5Results" };
        const qId = qMap[formId];
        const lKey = lMap[formId];
        nextProfile = { ...effectiveProfile };
        if (formId === "F1") {
          nextProfile.completedAt = result.forcedCompletedAt;
        } else if (qId && lKey) {
          if (Array.isArray(nextProfile.other)) {
            nextProfile.other = nextProfile.other.map((entry) =>
              entry && entry.questionnaire === qId
                ? {
                    ...entry,
                    date: result.forcedCompletedAt,
                    completedAt: result.forcedCompletedAt,
                    data:
                      entry.data && typeof entry.data === "object"
                        ? { ...entry.data, completedAt: result.forcedCompletedAt }
                        : entry.data,
                  }
                : entry
            );
          }
          if (nextProfile[lKey] && typeof nextProfile[lKey] === "object") {
            nextProfile[lKey] = { ...nextProfile[lKey], completedAt: result.forcedCompletedAt };
          }
        }
        setEffectiveProfile(nextProfile);
        if (onUserUpdated && user?.uid) {
          onUserUpdated(user.uid, { profile: nextProfile });
        }
      }
      setSyncResults((prev) => ({ ...prev, [formKey]: { ...result, verifying: true } }));
      verifySync(formKey, nextProfile);
    } else {
      setSyncResults((prev) => ({ ...prev, [formKey]: result }));
    }
  };

  const handlePullFormFromGcp = async (formKey) => {
    if (!gcpData || !user?.uid) return;
    const patched = buildPatchedFormFromGcp(formKey, effectiveProfile, gcpData);
    if (!patched) return;
    setSyncingForms((prev) => ({ ...prev, [formKey]: true }));
    try {
      const existingOther = Array.isArray(effectiveProfile?.other)
        ? effectiveProfile.other.filter((o) => o && typeof o === "object" && Boolean(o.questionnaire))
        : [];
      const wrappedEntry = {
        resourceType: "QuestionnaireResponse",
        questionnaire: patched.questionnaire,
        date: patched.completedAt,
        completedAt: patched.completedAt,
        data: patched.data,
      };
      const idx = existingOther.findIndex((o) => o && o.questionnaire === patched.questionnaire);
      if (idx >= 0) {
        existingOther[idx] = wrappedEntry;
      } else {
        existingOther.push(wrappedEntry);
      }
      const nextProfile = {
        ...effectiveProfile,
        other: existingOther,
        [patched.legacyKey]: patched.data,
        ...(patched.formId ? { [`pendingFhir${patched.formId}`]: false } : {}),
      };
      const fsPayload = {
        "profile.other": existingOther,
        [`profile.${patched.legacyKey}`]: patched.data,
        pendingFhirundefined: deleteField(),
        "profile.pendingFhirundefined": deleteField(),
      };
      if (patched.formId) {
        fsPayload[`pendingFhir${patched.formId}`] = false;
        fsPayload[`profile.pendingFhir${patched.formId}`] = false;
      }
      await updateDoc(doc(db, "users", user.uid), fsPayload);
      setEffectiveProfile(nextProfile);
      if (onUserUpdated) {
        onUserUpdated(user.uid, {
          profile: nextProfile,
          ...(patched.formId ? { [`pendingFhir${patched.formId}`]: false } : {}),
        });
      }
      setSyncResults((prev) => ({ ...prev, [formKey]: { ok: true, verified: true, verifying: false } }));
    } catch (err) {
      setSyncResults((prev) => ({ ...prev, [formKey]: { ok: false, error: err.message || "Error al actualizar Firestore" } }));
    }
    setSyncingForms((prev) => ({ ...prev, [formKey]: false }));
  };

  const handleSyncAll = async () => {
    for (const key of syncableForms) {
      await handleSyncForm(key, { forceTimestamp: false });
    }
  };

  const handleFlushLocalOutboxToFhir = async () => {
    if (!email || !user?.uid) return;
    setFlushingOutbox(true);
    setFlushFeedback(null);
    try {
      // 1. Flush pending subcollection measurements (users/{uid}/measurements)
      const flushRes = await flushPendingMeasurementsToGCP(user.uid, email);
      const flushedCount = typeof flushRes === "object" ? (flushRes.flushedCount || 0) : Number(flushRes || 0);

      // 2. Push F1 Patient demographics & baseline clinical fields to GCP FHIR
      let f1Ok = true;
      if (effectiveProfile && Object.keys(effectiveProfile).length > 0) {
        const f1Res = await adminResyncForm("F1", email, effectiveProfile, user.uid, { forceTimestamp: false });
        f1Ok = Boolean(f1Res?.ok);
      }

      // 3. Sync profile flat metrics & vitalSigns to FHIR
      let metricsOk = true;
      if (effectiveProfile && Object.keys(effectiveProfile).length > 0) {
        const metricsRes = await syncMetricsToGCP(email, effectiveProfile, { uid: user.uid });
        metricsOk = typeof metricsRes === "object" ? Boolean(metricsRes?.ok) : Boolean(metricsRes);
      }

      // 4. Reconcile clinical forms (F2..F5): push to GCP if Firebase is newer/missing in GCP, or pull from GCP if GCP is newer/missing in Firebase
      for (const key of ["hasF2", "hasF3", "hasF4", "hasF5"]) {
        const compItem = comparison.find((c) => c.key === key);
        const isPendingFlag = Boolean(
          user[`pendingFhir${FORM_SYNC_MAP[key]?.formId}`] ||
          effectiveProfile?.[`pendingFhir${FORM_SYNC_MAP[key]?.formId}`]
        );
        const gcpNewer = isFormGcpNewer(key);
        if (
          compItem &&
          compItem.fbVal === "true" &&
          (compItem.status === "missing_gcp" || ((compItem.status === "mismatch" || isPendingFlag) && !gcpNewer))
        ) {
          await handleSyncForm(key, { forceTimestamp: false });
        } else if (
          compItem &&
          compItem.gcpVal === "true" &&
          (gcpNewer || compItem.fbVal === "false" || compItem.fbVal === "Incompleto (ver Detalle)")
        ) {
          await handlePullFormFromGcp(key);
        }
      }

      // 5. Refresh GCP data & local subcollection stats (plus delayed re-check for FHIR indexing)
      const res = await queryBackend("/api/patients/everything", email);
      if (res.ok) setGcpData(res.data);
      await loadLocalOutboxStats();
      setTimeout(async () => {
        const resDelayed = await queryBackend("/api/patients/everything", email);
        if (resDelayed.ok) setGcpData(resDelayed.data);
      }, 2500);

      const nowIso = new Date().toISOString();
      if (f1Ok && metricsOk) {
        const updates = {
          fhirSyncStatus: "SYNCED",
          pendingFhirF1: false,
          pendingFhirF2: false,
          pendingFhirF3: false,
          pendingFhirF4: false,
          pendingFhirF5: false,
          pendingFhirMetrics: false,
          fhirReconciled: true,
          lastFhirSyncAt: nowIso,
          "profile.fhirSyncStatus": "SYNCED",
          "profile.pendingFhirF1": false,
          "profile.pendingFhirF2": false,
          "profile.pendingFhirF3": false,
          "profile.pendingFhirF4": false,
          "profile.pendingFhirF5": false,
          "profile.pendingFhirMetrics": false,
          "profile.lastFhirSyncAt": nowIso,
        };
        try {
          await Promise.resolve(updateDoc(doc(db, "users", user.uid), updates)).catch(() => {});
        } catch {}
        if (onUserUpdated) {
          onUserUpdated(user.uid, {
            fhirSyncStatus: "SYNCED",
            pendingFhirF1: false,
            pendingFhirF2: false,
            pendingFhirF3: false,
            pendingFhirF4: false,
            pendingFhirF5: false,
            pendingFhirMetrics: false,
            fhirReconciled: true,
            lastFhirSyncAt: nowIso,
          });
        }
        setFlushFeedback({
          ok: true,
          msg: `Sincronización completada con FHIR (${flushedCount} medición(es) en cola enviadas).`,
        });
      } else {
        setFlushFeedback({
          ok: false,
          msg: "Algunos datos continúan pendientes en la cola local. Verifique conexión con el backend.",
        });
      }
    } catch (err) {
      setFlushFeedback({
        ok: false,
        msg: `Error al sincronizar cola local: ${err.message || "Error desconocido"}`,
      });
    }
    setFlushingOutbox(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.4)" }}>
      <div
        className="bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ width: (tab === "raw" || tab === "detail") ? "90vw" : "640px", maxWidth: (tab === "raw" || tab === "detail") ? "1400px" : "640px", maxHeight: "85vh" }}
        data-testid="sync-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid #F1F5F9" }}>
          <div className="flex items-center gap-2">
            <ArrowLeftRight size={18} style={{ color: "#0284C7" }} />
            <div>
              <h3 className="text-sm font-bold" style={{ color: "#1E293B" }}>Estado de Sincronización Local (Firestore Outbox) ↔ FHIR</h3>
              <p className="text-[11px]" style={{ color: "#94A3B8" }}>{email || "Sin email"}</p>
            </div>
          </div>

          {/* Tabs */}
          {hasData && (
            <div className="flex items-center gap-1 rounded-lg p-0.5" style={{ background: "#F1F5F9" }}>
              {[
                { id: "summary", label: "Resumen" },
                { id: "detail", label: "Detalle" },
                { id: "raw", label: "Raw" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer"
                  style={{
                    background: tab === t.id ? "white" : "transparent",
                    color: tab === t.id ? "#1E293B" : "#94A3B8",
                    border: "none",
                    boxShadow: tab === t.id ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                  }}
                  data-testid={`tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer" style={{ border: "none", background: "none" }}>
            <X size={18} style={{ color: "#94A3B8" }} />
          </button>
        </div>

        {/* Outbox Local & FHIR Status Bar (Always visible when email exists) */}
        {email && (
          <div
            className="px-5 py-3 flex flex-wrap items-center justify-between gap-3"
            style={{ background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}
            data-testid="outbox-status-banner"
          >
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold"
                style={{ background: effectiveSyncBadge.bg, color: effectiveSyncBadge.color, border: `1px solid ${effectiveSyncBadge.border}` }}
              >
                {effectiveSyncBadge.status === "SYNCED" ? <CheckCircle2 size={13} /> : effectiveSyncBadge.status === "PENDING_FHIR" ? <AlertCircle size={13} /> : <ArrowLeftRight size={13} />}
                {effectiveSyncBadge.label}
              </span>

              <span className="px-2.5 py-1 rounded-md font-medium" style={{ background: "white", color: "#334155", border: "1px solid #E2E8F0" }}>
                Mediciones locales:{" "}
                {outboxStats.loading ? (
                  "..."
                ) : (
                  <>
                    <strong>{outboxStats.synced}</strong> sync /{" "}
                    <strong style={{ color: outboxStats.pending > 0 ? "#D97706" : "#16A34A" }}>
                      {outboxStats.pending} pendientes
                    </strong>
                  </>
                )}
              </span>

              {(syncInfo.pendingF1 || syncInfo.pendingMetrics) && (
                <span className="px-2.5 py-1 rounded-md font-semibold" style={{ background: "#FEF3C7", color: "#92400E" }}>
                  Cola activa: {[syncInfo.pendingF1 ? "F1" : null, syncInfo.pendingMetrics ? "Biomarcadores" : null].filter(Boolean).join(" + ")}
                </span>
              )}

              {syncInfo.lastSyncAt && (
                <span className="text-[11px]" style={{ color: "#64748B" }}>
                  Último sync FHIR: {new Date(syncInfo.lastSyncAt).toLocaleString("es-CO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {flushFeedback && (
                <span
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-md"
                  style={{
                    background: flushFeedback.ok ? "#DCFCE7" : "#FEF2F2",
                    color: flushFeedback.ok ? "#166534" : "#991B1B",
                  }}
                >
                  {flushFeedback.msg}
                </span>
              )}
              <button
                onClick={handleFlushLocalOutboxToFhir}
                disabled={flushingOutbox}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all hover:opacity-90"
                style={{
                  background: "#0284C7",
                  color: "white",
                  border: "none",
                  opacity: flushingOutbox ? 0.6 : 1,
                }}
                data-testid="flush-outbox-btn"
              >
                {flushingOutbox ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                Sincronizar datos locales a FHIR
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: "calc(85vh - 115px)" }}>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={20} className="animate-spin" style={{ color: "#0284C7" }} />
              <span className="text-xs ml-2" style={{ color: "#94A3B8" }}>Consultando GCP...</span>
            </div>
          ) : !email ? (
            <div className="text-center py-12 text-sm" style={{ color: "#94A3B8" }}>
              Este usuario no tiene email registrado. No se puede consultar GCP.
            </div>
          ) : gcpError ? (
            <div className="m-4 p-4 rounded-xl" style={{ background: "#FEF2F2" }}>
              <p className="text-sm font-semibold" style={{ color: "#991B1B" }}>Error al consultar GCP</p>
              <p className="text-xs mt-1" style={{ color: "#991B1B" }}>{gcpError}</p>
            </div>
          ) : tab === "raw" ? (
            /* ── RAW TAB (side-by-side, no diff) ── */
            <RawSideBySide firebaseJson={effectiveProfile || {}} gcpJson={gcpData?.profile || {}} />
          ) : tab === "detail" ? (
            /* ── DETAIL COMPARE TAB (semantic match by code) ── */
            <DetailCompareView
              fbProfile={effectiveProfile || {}}
              gcpData={gcpData}
              fallbackEmail={email}
              onSyncForm={handleSyncForm}
              onPullFormFromGcp={handlePullFormFromGcp}
              syncingForms={syncingForms}
              syncResults={syncResults}
            />
          ) : (
            /* ── SUMMARY TAB ── */
            <>
              {/* Sync pending banner */}
              {syncableForms.length > 0 && (
                <div className="flex items-center justify-between px-5 py-3" style={{ background: "#FFF7ED", borderBottom: "1px solid #FDBA7433" }}>
                  <div className="flex items-center gap-2">
                    <Upload size={14} style={{ color: "#EA580C" }} />
                    <span className="text-xs font-semibold" style={{ color: "#9A3412" }}>
                      {syncableForms.length} formulario{syncableForms.length > 1 ? "s" : ""} pendiente{syncableForms.length > 1 ? "s" : ""} de sincronizar
                    </span>
                  </div>
                  <button
                    onClick={handleSyncAll}
                    disabled={Object.values(syncingForms).some(Boolean)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer transition-all hover:opacity-90"
                    style={{ background: "#EA580C", color: "white", border: "none", opacity: Object.values(syncingForms).some(Boolean) ? 0.6 : 1 }}
                    data-testid="sync-all-pending-btn"
                  >
                    {Object.values(syncingForms).some(Boolean) ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Upload size={12} />
                    )}
                    Sincronizar todos
                  </button>
                </div>
              )}

              {/* Summary badges */}
              <div className="flex gap-3 px-5 py-4" style={{ background: "#F8FAFC" }}>
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: "#DCFCE7" }}>
                  <CheckCircle2 size={13} style={{ color: "#16A34A" }} />
                  <span className="text-xs font-bold" style={{ color: "#166534" }}>{matchCount} iguales</span>
                </div>
                {mismatchCount > 0 && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: "#FEF2F2" }}>
                    <AlertCircle size={13} style={{ color: "#DC2626" }} />
                    <span className="text-xs font-bold" style={{ color: "#991B1B" }}>{mismatchCount} diferentes</span>
                  </div>
                )}
                {missingGcp > 0 && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: "#FFFBEB" }}>
                    <MinusCircle size={13} style={{ color: "#D97706" }} />
                    <span className="text-xs font-bold" style={{ color: "#92400E" }}>{missingGcp} falta en GCP</span>
                  </div>
                )}
                <div className="ml-auto text-[11px] font-mono self-center" style={{ color: "#94A3B8" }}>
                  {total > 0 ? `${matchCount}/${total} sync` : "Sin datos"}
                </div>
              </div>

              {/* Comparison table */}
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: "#F1F5F9" }}>
                    <th className="text-left px-5 py-2 font-semibold" style={{ color: "#475569", width: "28%" }}>Campo</th>
                    <th className="text-left px-3 py-2 font-semibold" style={{ color: "#0284C7", width: "25%" }}>Firebase</th>
                    <th className="text-left px-3 py-2 font-semibold" style={{ color: "#059669", width: "25%" }}>GCP</th>
                    <th className="text-center px-3 py-2 font-semibold" style={{ color: "#475569", width: "22%" }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((c) => {
                    const sc = statusColor(c.status);
                    const isSyncable =
                      FORM_SYNC_MAP[c.key] &&
                      ((c.fbVal !== "false" && c.fbVal !== "-" && (c.status === "missing_gcp" || c.status === "mismatch")) ||
                        (c.gcpVal === "true" && (c.status === "missing_fb" || c.status === "mismatch")));
                    const gcpNewer =
                      (c.status === "mismatch" && isFormGcpNewer(c.key)) ||
                      c.status === "missing_fb" ||
                      (c.fbVal === "false" && c.gcpVal === "true") ||
                      c.fbVal === "Incompleto (ver Detalle)";
                    const isSyncing = syncingForms[c.key];
                    const syncResult = syncResults[c.key];
                    return (
                      <tr key={c.key} className="border-t" style={{ borderColor: "#F1F5F9", background: syncResult?.verified ? "#F0FDF4" : syncResult?.ok ? "#FFFBEB" : sc.bg }}>
                        <td className="px-5 py-2 font-medium" style={{ color: "#1E293B" }}>{c.label}</td>
                        <td className="px-3 py-2 font-mono" style={{ color: c.fbVal === "-" ? "#CBD5E1" : "#1E293B" }}>{c.fbVal}</td>
                        <td className="px-3 py-2 font-mono" style={{ color: c.gcpVal === "-" ? "#CBD5E1" : "#1E293B" }}>
                          {syncResult?.verified ? (
                            <span style={{ color: "#16A34A" }}>true</span>
                          ) : syncResult?.ok ? (
                            <span style={{ color: "#EA580C" }}>Enviado</span>
                          ) : c.gcpVal}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            {syncResult?.verified ? (
                              <span className="flex items-center gap-1 text-[10px] font-bold" style={{ color: "#16A34A" }}>
                                <CheckCircle2 size={12} /> Sincronizado
                              </span>
                            ) : syncResult?.ok && syncResult?.verifying ? (
                              <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: "#EA580C" }}>
                                <Loader2 size={10} className="animate-spin" /> Verificando...
                              </span>
                            ) : syncResult?.ok ? (
                              <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: "#D97706" }}>
                                <CheckCircle2 size={12} /> Enviado
                              </span>
                            ) : isSyncable && !syncResult ? (
                              gcpNewer ? (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handlePullFormFromGcp(c.key)}
                                    disabled={isSyncing}
                                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold cursor-pointer transition-all hover:opacity-90"
                                    style={{ background: "#059669", color: "white", border: "none", opacity: isSyncing ? 0.6 : 1 }}
                                    data-testid={`pull-btn-${c.key}`}
                                  >
                                    {isSyncing ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                                    Traer FHIR
                                  </button>
                                  <button
                                    onClick={() => handleSyncForm(c.key, { forceTimestamp: true })}
                                    disabled={isSyncing}
                                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold cursor-pointer transition-all hover:opacity-90"
                                    style={{ background: "#EA580C", color: "white", border: "none", opacity: isSyncing ? 0.6 : 1 }}
                                    data-testid={`sync-btn-${c.key}`}
                                  >
                                    {isSyncing ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
                                    Forzar
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleSyncForm(c.key, { forceTimestamp: false })}
                                  disabled={isSyncing}
                                  className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold cursor-pointer transition-all hover:opacity-90"
                                  style={{ background: "#EA580C", color: "white", border: "none", opacity: isSyncing ? 0.6 : 1 }}
                                  data-testid={`sync-btn-${c.key}`}
                                >
                                  {isSyncing ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
                                  Enviar
                                </button>
                              )
                            ) : syncResult && !syncResult.ok ? (
                              <span className="text-[10px] font-semibold" style={{ color: "#DC2626" }}>Error</span>
                            ) : (
                              statusIcon(c.status)
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Legend */}
              <div className="flex gap-4 px-5 py-3 text-[10px]" style={{ background: "#F8FAFC", borderTop: "1px solid #F1F5F9" }}>
                <span style={{ color: "#16A34A" }}><CheckCircle2 size={10} className="inline mr-0.5" /> Sincronizado</span>
                <span style={{ color: "#DC2626" }}><AlertCircle size={10} className="inline mr-0.5" /> Diferente</span>
                <span style={{ color: "#D97706" }}><MinusCircle size={10} className="inline mr-0.5" /> Falta en GCP</span>
                <span style={{ color: "#7C3AED" }}><MinusCircle size={10} className="inline mr-0.5" /> Falta en Firebase</span>
                <span style={{ color: "#94A3B8" }}><MinusCircle size={10} className="inline mr-0.5" /> Ambos vacíos</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────
export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const { impersonate } = useUserProfile();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterClientId, setFilterClientId] = useState("ALL");
  const [filterType, setFilterType] = useState("ALL");
  const [filterSync, setFilterSync] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState(null);
  const [permError, setPermError] = useState(false);
  const [syncUser, setSyncUser] = useState(null);

  const handleUserSyncUpdated = useCallback((uid, updates) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.uid === uid
          ? {
              ...u,
              ...updates,
              profile: u.profile ? { ...u.profile, ...updates } : u.profile,
            }
          : u
      )
    );
    setSyncUser((prev) =>
      prev && prev.uid === uid
        ? {
            ...prev,
            ...updates,
            profile: prev.profile ? { ...prev.profile, ...updates } : prev.profile,
          }
        : prev
    );
  }, []);
  
  // Bulk delete state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Set personal email modal (admin override)
  const [setEmailUser, setSetEmailUser] = useState(null); // user object
  const [adminEmailInput, setAdminEmailInput] = useState("");
  const [adminEmailSaving, setAdminEmailSaving] = useState(false);
  const [adminEmailError, setAdminEmailError] = useState(null);

  const toMs = (v) => {
    if (!v) return 0;
    if (typeof v === "string") return new Date(v).getTime() || 0;
    if (v.toDate) return v.toDate().getTime();
    if (v.seconds) return v.seconds * 1000;
    return 0;
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setPermError(false);
    try {
      const [snap, clientsSnap] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "clients")).catch(() => ({ docs: [] })),
      ]);
      const list = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
      list.sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
      setUsers(list);
      setClients(clientsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching users:", err);
      if (err.code === "permission-denied" || err.message?.includes("Missing or insufficient permissions")) {
        setPermError(true);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleRole = async (uid, currentRole, currentIsMedico) => {
    if (uid === currentUser?.uid) return;
    setUpdating(uid);
    let newRole = currentRole === "admin" ? "user" : "admin";
    if (newRole === "user" && currentIsMedico) {
      newRole = "medico";
    }
    try {
      await updateDoc(doc(db, "users", uid), { role: newRole });
      setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, role: newRole } : u)));
    } catch (err) { console.error("Error updating role:", err); }
    setUpdating(null);
  };

  const toggleIHO = async (uid, currentIsIHO) => {
    setUpdating(uid);
    const newIsIHO = !currentIsIHO;
    try {
      await updateDoc(doc(db, "users", uid), { isIHO: newIsIHO });
      setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, isIHO: newIsIHO } : u)));
    } catch (err) { console.error("Error toggling IHO:", err); }
    setUpdating(null);
  };

  const toggleMedico = async (uid, currentIsMedico, currentRole) => {
    setUpdating(uid);
    const newIsMedico = !currentIsMedico;
    const newRole = newIsMedico 
      ? (currentRole === "admin" ? "admin" : "medico")
      : (currentRole === "medico" ? "user" : currentRole);
    try {
      await updateDoc(doc(db, "users", uid), { 
        isMedico: newIsMedico,
        role: newRole
      });
      setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, isMedico: newIsMedico, role: newRole } : u)));
    } catch (err) { console.error("Error toggling Médico:", err); }
    setUpdating(null);
  };

  const toggleActive = async (uid, currentActive) => {
    if (uid === currentUser?.uid) return;
    setUpdating(uid);
    const newActive = currentActive === false ? true : false;
    try {
      await updateDoc(doc(db, "users", uid), { isActive: newActive });
      setUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, isActive: newActive } : u)));
    } catch (err) { console.error("Error updating status:", err); }
    setUpdating(null);
  };

  const handleImpersonate = (u) => {
    if (!u?.uid || u.uid === currentUser?.uid) return;
    const name =
      u.displayName ||
      `${u.profile?.firstName || u.profile?.nombres || ""} ${u.profile?.lastName || u.profile?.apellidos || ""}`.trim() ||
      u.profile?.name ||
      "";
    impersonate({
      uid: u.uid,
      name,
      email: u.email || u.profile?.email || "",
    });
    navigate("/dashboard");
  };

  const filtered = users.filter((u) => {
    if (filterClientId !== "ALL") {
      if (filterClientId === "NONE" && u.clientId) return false;
      if (filterClientId !== "NONE" && u.clientId !== filterClientId) return false;
    }
    if (filterType !== "ALL") {
      const cat = u.clientId ? clients.find((c) => c.id === u.clientId)?.category : "B2C";
      const userCat = cat || (u.clientId ? null : "B2C");
      if (userCat !== filterType) return false;
    }
    if (filterSync !== "ALL") {
      const sInfo = getUserFhirSyncInfo(u);
      if (sInfo.status !== filterSync) return false;
    }
    if (!search) return true;
    const s = search.toLowerCase();
    const name = (u.displayName || u.profile?.firstName || "").toLowerCase();
    const email = (u.email || u.profile?.email || "").toLowerCase();
    return name.includes(s) || email.includes(s);
  });

  const clientNameById = (id) => clients.find((c) => c.id === id)?.name || id;
  const clientCategoryById = (id) => clients.find((c) => c.id === id)?.category || null;

  const userTypeBadge = (u) => {
    if (!u.clientId) return { label: "B2C", color: "#16A34A", bg: "#DCFCE7" };
    const cat = clientCategoryById(u.clientId);
    if (cat === "B2G") return { label: "B2G", color: "#9333EA", bg: "#F3E8FF" };
    if (cat === "B2B") return { label: "B2B", color: "#0284C7", bg: "#E0F2FE" };
    return { label: "—", color: "#64748B", bg: "#F1F5F9" };
  };

  const adminCount = users.filter((u) => u.role === "admin").length;
  const activeCount = users.filter((u) => u.isActive !== false).length;
  const syncedFhirCount = users.filter((u) => getUserFhirSyncInfo(u).status === "SYNCED").length;
  const pendingFhirCount = users.filter((u) => getUserFhirSyncInfo(u).status === "PENDING_FHIR").length;

  const toDate = (v) => {
    if (!v) return null;
    if (v.toDate) return v.toDate();
    if (v.seconds) return new Date(v.seconds * 1000);
    if (typeof v === "string") { const d = new Date(v); return isNaN(d) ? null : d; }
    return null;
  };

  const fmtDate = (raw) => {
    const d = toDate(raw);
    if (!d) return "-";
    return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
  };

  const fmtDateTime = (raw) => {
    const d = toDate(raw);
    if (!d) return "-";
    return d.toLocaleString("es-CO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  // ── Bulk delete handlers ──
  const toggleSelectUser = (uid) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(uid)) {
        newSet.delete(uid);
      } else {
        newSet.add(uid);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === filtered.length) {
      setSelectedUsers(new Set());
    } else {
      const allUids = filtered
        .filter((u) => u.uid !== currentUser?.uid) // Don't allow selecting current user
        .map((u) => u.uid);
      setSelectedUsers(new Set(allUids));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedUsers.size === 0) return;
    setDeleting(true);
    
    try {
      const { deleteUser } = await import("firebase/auth");
      const { deleteDoc } = await import("firebase/firestore");
      const auth = (await import("@/firebase")).auth;
      
      // Delete each user
      const deletePromises = Array.from(selectedUsers).map(async (uid) => {
        try {
          // Delete from Firestore
          await deleteDoc(doc(db, "users", uid));
          
          // Note: Deleting from Auth requires admin SDK (backend)
          // For now, we only delete from Firestore
          // TODO: Call backend endpoint to delete from Auth
          
          return { uid, success: true };
        } catch (error) {
          console.error(`Error deleting user ${uid}:`, error);
          return { uid, success: false, error };
        }
      });
      
      const results = await Promise.all(deletePromises);
      const successCount = results.filter((r) => r.success).length;
      
      // Remove deleted users from state
      setUsers((prev) => prev.filter((u) => !selectedUsers.has(u.uid)));
      
      // Clear selection
      setSelectedUsers(new Set());
      setShowDeleteConfirm(false);
      
      alert(`✅ ${successCount} usuario(s) eliminado(s) correctamente`);
    } catch (error) {
      console.error("Error during bulk delete:", error);
      alert(`❌ Error al eliminar usuarios: ${error.message}`);
    }
    
    setDeleting(false);
  };

  return (
    <div>
      {/* Sync Modal */}
      {syncUser && (
        <SyncModal
          user={syncUser}
          onClose={() => setSyncUser(null)}
          onUserUpdated={handleUserSyncUpdated}
        />
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="stat-total">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#E0F2FE" }}>
              <Users size={20} style={{ color: "#0284C7" }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: "#1E293B" }}>{users.length}</p>
              <p className="text-xs" style={{ color: "#64748B" }}>Total usuarios</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="stat-admins">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#FEF3C7" }}>
              <ShieldCheck size={20} style={{ color: "#D97706" }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: "#1E293B" }}>{adminCount}</p>
              <p className="text-xs" style={{ color: "#64748B" }}>Administradores</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="stat-active">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#DCFCE7" }}>
              <UserCheck size={20} style={{ color: "#16A34A" }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: "#1E293B" }}>{activeCount}</p>
              <p className="text-xs" style={{ color: "#64748B" }}>Activos</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="stat-fhir-sync">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: pendingFhirCount > 0 ? "#FEF3C7" : "#DCFCE7" }}>
              <ArrowLeftRight size={20} style={{ color: pendingFhirCount > 0 ? "#D97706" : "#16A34A" }} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: "#1E293B" }}>
                {syncedFhirCount}
                {pendingFhirCount > 0 && (
                  <span className="text-xs font-bold ml-1.5 px-1.5 py-0.5 rounded" style={{ background: "#FEF3C7", color: "#B45309" }}>
                    {pendingFhirCount} en cola
                  </span>
                )}
              </p>
              <p className="text-xs" style={{ color: "#64748B" }}>Sincronizados FHIR</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search + Bulk Actions + Refresh */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
          <input
            type="text"
            placeholder="Buscar por nombre o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm"
            style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
            data-testid="users-search-input"
          />
        </div>

        {/* Filter by client */}
        <select
          value={filterClientId}
          onChange={(e) => setFilterClientId(e.target.value)}
          className="px-3 py-2.5 rounded-lg border text-sm cursor-pointer"
          style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
          data-testid="users-client-filter"
        >
          <option value="ALL">Todos los clientes</option>
          <option value="NONE">Sin cliente (B2C)</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        {/* Filter by user type */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2.5 rounded-lg border text-sm cursor-pointer"
          style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
          data-testid="users-type-filter"
        >
          <option value="ALL">Todos los tipos</option>
          <option value="B2C">B2C</option>
          <option value="B2B">B2B</option>
          <option value="B2G">B2G</option>
        </select>

        {/* Filter by FHIR sync state */}
        <select
          value={filterSync}
          onChange={(e) => setFilterSync(e.target.value)}
          className="px-3 py-2.5 rounded-lg border text-sm cursor-pointer"
          style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
          data-testid="users-sync-filter"
        >
          <option value="ALL">Sync FHIR: Todos</option>
          <option value="SYNCED">Sincronizado FHIR</option>
          <option value="PENDING_FHIR">Pendiente en Outbox</option>
          <option value="UNVERIFIED">Por verificar en FHIR</option>
          <option value="EMPTY">Sin datos clínicos</option>
        </select>
        
        {/* Bulk delete button */}
        {selectedUsers.size > 0 && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all hover:opacity-90"
            style={{ 
              background: "#DC2626", 
              color: "white",
              opacity: deleting ? 0.6 : 1,
              cursor: deleting ? "not-allowed" : "pointer"
            }}
            data-testid="bulk-delete-btn"
          >
            <Trash2 size={16} />
            Eliminar {selectedUsers.size}
          </button>
        )}
        
        <button
          onClick={fetchUsers}
          className="p-2.5 rounded-lg border transition-colors hover:bg-gray-50"
          style={{ borderColor: "#E2E8F0" }}
          data-testid="users-refresh-btn"
        >
          <RefreshCw size={16} style={{ color: "#64748B" }} />
        </button>
      </div>

      {/* Permission error banner */}
      {permError && (
        <div className="mb-4 p-4 rounded-xl border text-sm" style={{ background: "#FEF3C7", borderColor: "#FCD34D", color: "#92400E" }} data-testid="users-perm-error">
          <p className="font-semibold mb-1">Permisos insuficientes en Firestore</p>
          <p>Actualiza las reglas en la consola de Firebase para permitir lectura a administradores.</p>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin" style={{ color: "#00628F" }} />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr style={{ background: "#F8FAFC" }}>
                  <th className="px-4 py-3 w-12">
                    <input
                      type="checkbox"
                      checked={selectedUsers.size > 0 && selectedUsers.size === filtered.filter(u => u.uid !== currentUser?.uid).length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded cursor-pointer"
                      style={{ accentColor: "#0284C7" }}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Usuario</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Email</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Tipo</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Cliente</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Rol</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Estado</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Sync FHIR</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Registro</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Acceso</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const name = u.displayName || u.profile?.name || u.profile?.firstName || "-";
                  const email = u.email || u.profile?.email || "-";
                  const role = u.role || "user";
                  const active = u.isActive !== false;
                  const isSelf = u.uid === currentUser?.uid;
                  const syncInfo = getUserFhirSyncInfo(u);

                  return (
                    <tr key={u.uid} className="border-t transition-colors hover:bg-gray-50/50" style={{ borderColor: "#F1F5F9" }} data-testid={`user-row-${u.uid}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(u.uid)}
                          onChange={() => toggleSelectUser(u.uid)}
                          disabled={isSelf}
                          className="w-4 h-4 rounded cursor-pointer"
                          style={{ 
                            accentColor: "#0284C7",
                            opacity: isSelf ? 0.3 : 1,
                            cursor: isSelf ? "not-allowed" : "pointer"
                          }}
                          data-testid={`select-user-${u.uid}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium" style={{ color: "#1E293B" }}>{name}</span>
                          {isSelf && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#E0F2FE", color: "#0284C7" }}>T&uacute;</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3" style={{ color: "#64748B" }}>{email}</td>
                      <td className="px-4 py-3 text-center">
                        {(() => {
                          const t = userTypeBadge(u);
                          return (
                            <span
                              className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full"
                              style={{ background: t.bg, color: t.color }}
                              data-testid={`user-type-${u.uid}`}
                            >
                              {t.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        {u.clientId ? (
                          <span className="inline-flex items-center text-[11px] font-semibold px-2 py-1 rounded-md" style={{ background: "#E0F2FE", color: "#0284C7" }} data-testid={`user-client-${u.uid}`}>
                            {clientNameById(u.clientId)}
                          </span>
                        ) : (
                          <span className="text-[11px]" style={{ color: "#94A3B8" }}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
                            style={role === "admin" ? { background: "#FEF3C7", color: "#92400E" } : { background: "#F1F5F9", color: "#64748B" }}
                            data-testid={`user-role-${u.uid}`}>
                            {role === "admin" ? <ShieldCheck size={12} /> : null}
                            {role === "admin" ? "Admin" : "Usuario"}
                          </span>
                          {u.isIHO && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                              style={{ background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" }}
                              data-testid={`user-iho-badge-${u.uid}`}>
                              <ShieldAlert size={9} />
                              IHO
                            </span>
                          )}
                          {(u.isMedico || role === "medico") && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                              style={{ background: "#ECFDF5", color: "#0D9488", border: "1px solid #A7F3D0" }}
                              data-testid={`user-medico-badge-${u.uid}`}>
                              <Stethoscope size={9} />
                              Médico
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
                          style={active ? { background: "#DCFCE7", color: "#166534" } : { background: "#FEE2E2", color: "#991B1B" }}
                          data-testid={`user-status-${u.uid}`}>
                          {active ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <button
                            onClick={() => setSyncUser(u)}
                            title="Clic para inspeccionar y sincronizar datos locales con FHIR"
                            className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full cursor-pointer transition-all hover:opacity-85"
                            style={{
                              background: syncInfo.bg,
                              color: syncInfo.color,
                              border: `1px solid ${syncInfo.border}`,
                            }}
                            data-testid={`fhir-sync-badge-${u.uid}`}
                          >
                            {syncInfo.status === "SYNCED" ? (
                              <CheckCircle2 size={11} />
                            ) : syncInfo.status === "PENDING_FHIR" ? (
                              <AlertCircle size={11} />
                            ) : (
                              <ArrowLeftRight size={11} />
                            )}
                            {syncInfo.label}
                          </button>
                          {syncInfo.lastSyncAt && (
                            <span className="text-[10px]" style={{ color: "#94A3B8" }}>
                              {fmtDateTime(syncInfo.lastSyncAt)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3" style={{ color: "#64748B" }}>{fmtDate(u.createdAt || u.profile?.createdAt)}</td>
                      <td className="px-4 py-3" style={{ color: "#64748B" }}>{fmtDateTime(u.lastLogin)}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          {/* Check Sync button — always available */}
                          <button
                            onClick={() => setSyncUser(u)}
                            title="Ver estado de sincronización local (Firestore Outbox) vs FHIR"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold transition-colors hover:bg-blue-50 cursor-pointer"
                            style={{ border: "1px solid #BAE6FD", background: "#F0F9FF", color: "#0284C7" }}
                            data-testid={`check-sync-${u.uid}`}
                          >
                            <ArrowLeftRight size={13} style={{ color: "#0284C7" }} />
                            Sync FHIR
                          </button>

                          {/* Impersonate button — not for self */}
                          {!isSelf && (
                            <button
                              onClick={() => handleImpersonate(u)}
                              title={`Navegar como ${name !== "-" ? name : email}`}
                              className="p-1.5 rounded-md transition-colors hover:bg-purple-50 cursor-pointer"
                              style={{ border: "none", background: "none" }}
                              data-testid={`impersonate-${u.uid}`}
                            >
                              <Eye size={15} style={{ color: "#9333EA" }} />
                            </button>
                          )}

                          {/* Role, Active & Info toggles */}
                          {updating === u.uid ? (
                            <Loader2 size={14} className="animate-spin" style={{ color: "#94A3B8" }} />
                          ) : (
                            <>
                              {/* Set personal email */}
                              <button
                                onClick={() => { setSetEmailUser(u); setAdminEmailInput(u.personalEmail || ""); setAdminEmailError(null); }}
                                title={u.personalEmail ? `Email personal: ${u.personalEmail}` : "Sin email personal — clic para configurar"}
                                className="p-1.5 rounded-md transition-colors hover:bg-yellow-50"
                                data-testid={`set-personal-email-${u.uid}`}
                              >
                                <Mail size={15} style={{ color: u.personalEmail ? "#D97706" : "#CBD5E1" }} />
                              </button>

                              {/* Toggle admin role — strictly not for self to prevent self-demotion */}
                              {!isSelf && (
                                <button
                                  onClick={() => toggleRole(u.uid, role, u.isMedico || role === "medico")}
                                  title={role === "admin" ? "Quitar admin" : "Hacer admin"}
                                  className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                                  data-testid={`toggle-role-${u.uid}`}
                                >
                                  {role === "admin" ? <ShieldOff size={15} style={{ color: "#D97706" }} /> : <ShieldCheck size={15} style={{ color: "#94A3B8" }} />}
                                </button>
                              )}

                              {/* Toggle IHO status — allowed for self */}
                              <button
                                onClick={() => toggleIHO(u.uid, u.isIHO)}
                                title={u.isIHO ? "Quitar rol IHO" : "Asignar como IHO (Oficial de Salud)"}
                                className="p-1.5 rounded-md transition-colors hover:bg-blue-50"
                                data-testid={`toggle-iho-${u.uid}`}
                              >
                                <ShieldAlert size={15} style={{ color: u.isIHO ? "#1D4ED8" : "#CBD5E1" }} />
                              </button>

                              {/* Toggle Médico status — allowed for self */}
                              <button
                                onClick={() => toggleMedico(u.uid, u.isMedico || role === "medico", role)}
                                title={u.isMedico || role === "medico" ? "Quitar rol de Médico" : "Asignar como Médico de Familia"}
                                className="p-1.5 rounded-md transition-colors hover:bg-teal-50"
                                data-testid={`toggle-medico-${u.uid}`}
                              >
                                <Stethoscope size={15} style={{ color: (u.isMedico || role === "medico") ? "#0D9488" : "#CBD5E1" }} />
                              </button>

                              {/* Toggle account status — strictly not for self to prevent lockout */}
                              {!isSelf && (
                                <button
                                  onClick={() => toggleActive(u.uid, u.isActive)}
                                  title={active ? "Desactivar cuenta" : "Activar cuenta"}
                                  className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                                  data-testid={`toggle-active-${u.uid}`}
                                >
                                  {active ? <UserX size={15} style={{ color: "#94A3B8" }} /> : <UserCheck size={15} style={{ color: "#16A34A" }} />}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center py-12" style={{ color: "#94A3B8" }}>
                      {search ? "No se encontraron usuarios" : "Sin usuarios registrados"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Set Personal Email Modal (admin override) */}
      {setEmailUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" data-testid="admin-set-email-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#FEF3C7" }}>
                <Mail size={18} style={{ color: "#D97706" }} />
              </div>
              <div>
                <h3 className="font-bold" style={{ color: "#1E293B" }}>Email personal de respaldo</h3>
                <p className="text-xs" style={{ color: "#64748B" }}>
                  {setEmailUser.displayName || setEmailUser.email}
                </p>
              </div>
            </div>
            <p className="text-xs mb-3" style={{ color: "#64748B" }}>
              Este email permite al usuario acceder a sus datos si pierde su cuenta corporativa.
            </p>
            <input
              type="email"
              value={adminEmailInput}
              onChange={(e) => { setAdminEmailInput(e.target.value); setAdminEmailError(null); }}
              placeholder="email@personal.com"
              className="w-full px-3 py-2.5 rounded-xl border text-sm mb-1"
              style={{ borderColor: adminEmailError ? "#FCA5A5" : "#E2E8F0", color: "#1E293B" }}
              autoFocus
              data-testid="admin-email-input"
            />
            {adminEmailError && (
              <p className="text-xs mb-2" style={{ color: "#DC2626" }}>{adminEmailError}</p>
            )}
            <p className="text-[11px] mb-4" style={{ color: "#94A3B8" }}>
              Deja vacío para eliminar el email personal registrado.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => { setSetEmailUser(null); setAdminEmailError(null); }}
                className="flex-1 py-2 rounded-xl text-sm border"
                style={{ borderColor: "#E2E8F0", color: "#64748B" }}
              >
                Cancelar
              </button>
              <button
                disabled={adminEmailSaving}
                onClick={async () => {
                  const val = adminEmailInput.trim().toLowerCase();
                  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (val && !EMAIL_RE.test(val)) {
                    setAdminEmailError("Email inválido");
                    return;
                  }
                  setAdminEmailSaving(true);
                  try {
                    await setDoc(doc(db, "users", setEmailUser.uid), {
                      personalEmail: val || null,
                      personalEmailVerified: false,
                    }, { merge: true });
                    setUsers((prev) => prev.map((u) =>
                      u.uid === setEmailUser.uid ? { ...u, personalEmail: val || null } : u
                    ));
                    setSetEmailUser(null);
                  } catch {
                    setAdminEmailError("Error al guardar");
                  } finally {
                    setAdminEmailSaving(false);
                  }
                }}
                className="flex-1 py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-1"
                style={{ background: "#D97706", color: "white", border: "none", cursor: adminEmailSaving ? "wait" : "pointer" }}
                data-testid="admin-email-save-btn"
              >
                {adminEmailSaving ? <Loader2 size={13} className="animate-spin" /> : null}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#FEE2E2" }}>
                <Trash2 size={24} style={{ color: "#DC2626" }} />
              </div>
              <div>
                <h3 className="text-lg font-bold" style={{ color: "#1E293B" }}>Eliminar Usuarios</h3>
                <p className="text-sm" style={{ color: "#64748B" }}>Esta acción no se puede deshacer</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm mb-3" style={{ color: "#475569" }}>
                Estás a punto de eliminar <strong>{selectedUsers.size} usuario(s)</strong>:
              </p>
              <div className="max-h-48 overflow-y-auto p-3 rounded-lg" style={{ background: "#F8FAFC" }}>
                <ul className="space-y-2 text-sm">
                  {Array.from(selectedUsers).map((uid) => {
                    const user = users.find((u) => u.uid === uid);
                    const name = user?.displayName || user?.profile?.firstName || "-";
                    const email = user?.email || user?.profile?.email || "-";
                    return (
                      <li key={uid} className="flex items-center gap-2" style={{ color: "#64748B" }}>
                        <UserX size={14} />
                        <span className="font-medium">{name}</span>
                        <span className="text-xs">({email})</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-lg border transition-all hover:bg-gray-50"
                style={{ borderColor: "#E2E8F0", color: "#64748B" }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteSelected}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-all hover:opacity-90"
                style={{ 
                  background: "#DC2626", 
                  color: "white",
                  opacity: deleting ? 0.6 : 1,
                  cursor: deleting ? "not-allowed" : "pointer"
                }}
              >
                {deleting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Eliminar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
